// src/pages/CashierSalesPage.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { translateDynamic, getTransliteration, getProductName, getEntityTranslation } from '../../context/LanguageContext';

import { employeeService, transactionService, orderTrackingService, paymentMethodService, occupationService, modifierService } from '../../services/api';
import api from '../../services/api';
import Modal from '../../components/common/Modal';

import Alert from '../../components/common/Alert';
import SelectableCard from '../../components/common/SelectableCard';
import { getCategoryIcon } from '../../constants/categoryIcons';
import { renderEntityImage } from '../../utils/imageUtils';
import { selectionProblemsFrom, serverMessageFrom } from '../../utils/apiErrors';
// One builder for every POS surface. This page kept a private copy that never
// learned about line_id/parent_line_id/group_id, so nested modifiers reached
// the server flat and a valid sale was rejected at payment with a 400.
import { createItemsWithAddons } from '../../pos-app/surfaces/cashier/lib/transactionFlow';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import StartOfDayScreen from '../../components/pos/StartOfDayScreen';
import EndOfDayFlow from '../../components/pos/EndOfDayFlow';
import tenantStorage from '../../services/tenantStorage';
import host from '../../host';

import SplitBillManager from '../../components/pos/SplitBillManager';
import StaffAllowanceManager from '../../components/pos/StaffAllowanceManager';
import { createPortal } from 'react-dom';
import TransactionsPane from '../../components/pos/refunds/TransactionsPane';
import VisualTableSelector, { resolveOccupation } from '../../components/pos/VisualTableSelector';
import OrdersPanel from '../../components/pos/OrdersPanel';

import DeletionConfirmModal from '../../components/common/DeletionConfirmModal';
import PaymentConfirmModal from '../../components/pos/modals/PaymentConfirmModal';
import AddonSelectionModal from '../../components/pos/modals/AddonSelectionModal';
import EditingProtectionModal from '../../components/pos/modals/EditingProtectionModal';
import TableOrderModal from '../../components/pos/modals/TableOrderModal';
import ChangeTableModal from '../../components/pos/modals/ChangeTableModal';
import useDeletionConfirm from '../../hooks/useDeletionConfirm';
import useBusinessDay from '../../hooks/pos/useBusinessDay';
import useProductCatalog from '../../hooks/pos/useProductCatalog';
import useDiningSetup from '../../hooks/pos/useDiningSetup';
import usePendingOrders from '../../hooks/pos/usePendingOrders';
import useOrderBuilder from '../../hooks/pos/useOrderBuilder';
import useSessionManager from '../../hooks/pos/useSessionManager';
import useCashierDetection from '../../hooks/pos/useCashierDetection';
import usePromoEvaluator from '../../hooks/pos/usePromoEvaluator';
import useIsMobile from '../../hooks/useIsMobile';
import ManagerGate from '../../components/pos/ManagerGate';
import { catalogueOrderService } from '../../services/api';
import {
    createPaidOrder,
    clearPaidOrder,
    deletePaidOrderAction,
} from '../../utils/paidOrders';

import {
    deductStockForPendingOrder,
    revertStockDeduction,
    deductStockForAddedItems,
    createOrderTracking,
    updateOrderTrackingRecords,
    calculateNewItemsForTracking,
    deductInventoryForAdditionalItems
} from '../../utils/OrderHelpers';
import {
    generateReceiptHTML,
    printReceipts,
    printWithIframe,
} from '../../utils/PrintingUtils';

import {
    createTransactionFromOrder
} from '../../utils/OrderManagement';

const SalesPage = () => {
    const navigate = useNavigate();
    const {
        tenantConfig,
        getBrandName,
        getCurrency,
        getTenantSetting,
        printerConfigured,
        tenantId,
        authType,
        employeeId,
        username,
        logout,
        setAuthenticatedState,
    } = useAuth();
    const { t, language, dynamicTranslations, translationsLoaded, createReceiptTranslator, translationDetails } = useLanguage();
    const isMobile = useIsMobile();
    const [mobileCartOpen, setMobileCartOpen] = useState(false);

    // Get tenant-specific settings
    const brandName = getBrandName();
    const currency = getCurrency();

    const [configLoaded, setConfigLoaded] = useState(false);

    // Dynamic tenant-based state
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
    const {
        diningOptions, diningOptionObjects, fullDiningOptions, availableTableNumbers,
        selectedDiningOption, setSelectedDiningOption,
        tableNumber, setTableNumber,
        tableNumberConfirmed, setTableNumberConfirmed,
        tableViewMode, setTableViewMode,
        loadDiningOptions, refreshDiningOptions,
    } = useDiningSetup(tenantConfig);
    // Product and transaction state (products, categories, filtering handled by useProductCatalog)
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Modal and UI state
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [editingOrderKey, setEditingOrderKey] = useState(null);

    const [paymentConfirmModalOpen, setPaymentConfirmModalOpen] = useState(false);
    const [pendingTransaction, setPendingTransaction] = useState(null);
    const [editingProtectionModalOpen, setEditingProtectionModalOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [pendingActionData, setPendingActionData] = useState(null);
    const [editingWarningShown, setEditingWarningShown] = useState(false);

    const [currentOrderId, setCurrentOrderId] = useState(null); // Current order being edited
    const [orderSummaryExpanded, setOrderSummaryExpanded] = useState(false);
    const [currentConfirmOrder, setCurrentConfirmOrder] = useState(null);
    const splitBillRef = useRef(null);
    const [selectedModalPaymentMethod, setSelectedModalPaymentMethod] = useState(null);

    const [tableOrderModalOpen, setTableOrderModalOpen] = useState(false);
    const [selectedTableOrderKey, setSelectedTableOrderKey] = useState(null);
    const [selectedTableOrder, setSelectedTableOrder] = useState(null);
    const [ordersPanelOpen, setOrdersPanelOpen] = useState(false);

    const [changeTableModalOpen, setChangeTableModalOpen] = useState(false);
    const [orderToChangeTable, setOrderToChangeTable] = useState(null);
    const [newTableNumber, setNewTableNumber] = useState('');
    const [newDiningOption, setNewDiningOption] = useState('');

    const [printingEnabled, setPrintingEnabled] = useState(true);
    const [printerWarning, setPrinterWarning] = useState('');
    const [printingConfigLoaded, setPrintingConfigLoaded] = useState(false);
    const [receiptTranslationsEnabled, setReceiptTranslationsEnabled] = useState(true);
    const [translationsConfigLoaded, setTranslationsConfigLoaded] = useState(false);
    const [receiptTranslationLanguage, setReceiptTranslationLanguage] = useState('');
    const [primaryColor, setPrimaryColor] = useState('#0ea5e9');
    const [activeOccupation, setActiveOccupation] = useState(null);
    const [activeOccupations, setActiveOccupations] = useState([]);
    const [showMoreMenu, setShowMoreMenu] = useState(false);
    const [showTransactions, setShowTransactions] = useState(false);
    const [showFloorPlanModal, setShowFloorPlanModal] = useState(false);
    const [floorPlanDO, setFloorPlanDO] = useState(null);
    const [pillHighlight, setPillHighlight] = useState(false);
    const [billNotifications, setBillNotifications] = useState([]);
    const seenBillRequests = useRef(new Set());
    const [openStaffAllowance, setOpenStaffAllowance] = useState(false);
    const deletionConfirm = useDeletionConfirm();

    const {
        isBusinessDay, setIsBusinessDay,
        businessDaySalesDate, setBusinessDaySalesDate,
        updateBusinessDayState,
    } = useBusinessDay();

    const eodMode = getTenantSetting('eod_mode', 'per_store');
    const {
        activeSession, showSoD, showEoD, setShowEoD, sessionLoading,
        handleSessionStarted, handleEndSession, handleSessionClosed,
    } = useSessionManager({ employeeId, eodMode, setIsBusinessDay, setBusinessDaySalesDate, logout });

    const { cashierName, setCashierName, cashiers, setCashiers } = useCashierDetection({
        authType, employeeId, username, configLoaded: !!tenantConfig && configLoaded,
    });

    const {
        products, setProducts, filteredProducts,
        categories, categoryObjects, categoryMap, categoryIcons, printableCategories,
        selectedCategory, setSelectedCategory,
        subcategories, selectedSubcategory, setSelectedSubcategory,
        bulkCalculatedStock, setBulkCalculatedStock,
        productSearch, setProductSearch,
        loadCatalog, refreshBulkStock,
    } = useProductCatalog(tenantConfig);

    const {
        selectedProducts, setSelectedProducts,
        notes, setNotes,
        addServiceCharge, setAddServiceCharge,
        addTax, setAddTax,
        discountAmount, setDiscountAmount,
        discountMode, setDiscountMode,
        discountType, setDiscountType,
        discountPercentage, setDiscountPercentage,
        payableAmount, setPayableAmount,
        showDiscountPanel, setShowDiscountPanel,
        showNotesPanel, setShowNotesPanel,
        productAddons, setProductAddons,
        addonModalOpen, setAddonModalOpen,
        currentProduct, setCurrentProduct,
        currentProductGroups, activeCatalog, modifierTree,
        toggleModifier, modifierUnmet, modifierFlagUnmet, modifierLineTotal,
        memoizedTotals,
        handleCalculateTotals,
        handleToggleProduct,
        handleUpdateProductQuantity,
        handleRemoveProduct,
        handleUpdateProductNotes,
        handleProductWithAddons,
        addProductWithAddons,
        resetFormState,
        setModifierCatalog,
    } = useOrderBuilder({
        tenantConfig,
        selectedDiningOption,
        diningOptionObjects,
        bulkCalculatedStock,
        products,
        setError,
        t,
    });

    // ── Manager-gated actions (client request 2026-08-26) ────────────────────
    // Discounts and pending-order deletion both require a manager PIN. The
    // typed discount lives in LOCAL draft state so the cart total cannot move
    // until /manager-approvals/verify has confirmed a manager; the delete key
    // is held here until the same gate approves, and only then does the
    // existing reason modal open. Mirrors the native surface (CartPanel.js /
    // OrdersPanelNative.js).
    // Menu reorder (client request 2026-08-26) — manager-gated, and offered on
    // this till as well as the native one. Move-up/down rather than touch drag:
    // unambiguous on a tablet, impossible to mistake for a tap that adds an
    // item to a customer's order, and usable under assistive tech.
    const [reorderGateOpen, setReorderGateOpen] = useState(false);
    const [reordering, setReordering] = useState(false);
    const [localOrder, setLocalOrder] = useState(null);
    // Category order is tracked separately from the product order: they are
    // different entities server-side, and moving a chip must not renumber the
    // products under it. Mirrors CatalogBrowse.js on the native till — this
    // client is on THIS surface, so parity is the point, not a nicety.
    const [localCategoryOrder, setLocalCategoryOrder] = useState(null);

    const [discountDraft, setDiscountDraft] = useState('');
    const [pendingDiscount, setPendingDiscount] = useState(null);
    const [pendingDeleteKey, setPendingDeleteKey] = useState(null);

    // ── Promotion evaluation ─────────────────────────────────────────────────
    const {
        appliedPromo,
        allEligibleOptions: promoAllEligible,
        showPromoModal,
        setShowPromoModal,
        selectPromo,
        removePromo,
        resetPromo,
        promoDiscountAmount,
    } = usePromoEvaluator({
        selectedProducts,
        selectedDiningOption,
        subtotal: memoizedTotals.subtotal,
        enabled: !!tenantConfig,
    });

    // ESC key navigation — close the top-most modal, else unwind the selection chips
    useEffect(() => {
        const handleEscKey = (e) => {
            if (e.key !== 'Escape') return;

            // Ignore ESC if a print dialog was just closed
            if (window.__aqanaPrintDialogOpen) return;

            // If a modal or panel is open, close it
            if (ordersPanelOpen) { setOrdersPanelOpen(false); return; }
            if (confirmModalOpen) { setConfirmModalOpen(false); return; }
            if (addonModalOpen) { setAddonModalOpen(false); return; }
            if (paymentConfirmModalOpen) { setPaymentConfirmModalOpen(false); return; }
            if (editingProtectionModalOpen) { setEditingProtectionModalOpen(false); return; }
            if (tableOrderModalOpen) { setTableOrderModalOpen(false); return; }
            if (changeTableModalOpen) { setChangeTableModalOpen(false); return; }
            if (deletionConfirm.isOpen) { deletionConfirm.handleCancel(); return; }
            if (showDiscountPanel) { setShowDiscountPanel(false); return; }
            if (showNotesPanel) { setShowNotesPanel(false); return; }
            if (mobileCartOpen) { setMobileCartOpen(false); return; }

            // No modal open — unwind the selection chips: subcategory → category → search
            if (selectedSubcategory) {
                setSelectedSubcategory(null);
            } else if (selectedCategory) {
                setSelectedCategory(null);
            } else if (productSearch) {
                setProductSearch('');
            }
        };

        window.addEventListener('keydown', handleEscKey);
        return () => window.removeEventListener('keydown', handleEscKey);
    }, [
        selectedCategory, selectedSubcategory, productSearch,
        ordersPanelOpen, confirmModalOpen, addonModalOpen, paymentConfirmModalOpen,
        editingProtectionModalOpen, tableOrderModalOpen, changeTableModalOpen,
        deletionConfirm.isOpen, showDiscountPanel, showNotesPanel, mobileCartOpen
    ]);

    // Close the mobile cart sheet if the viewport flips out of mobile
    useEffect(() => {
        if (!isMobile) setMobileCartOpen(false);
    }, [isMobile]);

    // Detect dine-in orders that have a real table number assigned
    const isDineInWithTable = !!(
        selectedDiningOption &&
        tableNumber &&
        Array.isArray(availableTableNumbers[selectedDiningOption])
    );

    // Fetch active occupation whenever the dine-in table selection changes
    useEffect(() => {
        if (!isDineInWithTable) {
            setActiveOccupation(null);
            return;
        }
        const diningOptionObj = diningOptionObjects[selectedDiningOption];
        if (!diningOptionObj?.id) return;
        occupationService.getByTable(diningOptionObj.id, tableNumber)
            .then(occ => setActiveOccupation(occ || null))
            .catch(() => setActiveOccupation(null));
    }, [isDineInWithTable, selectedDiningOption, tableNumber, diningOptionObjects]);

    // Request bill for the current dine-in table (prints receipt preview + signals kitchen)
    const handleRequestBill = async () => {
        const diningOptionObj = diningOptionObjects[selectedDiningOption];
        let occupationId = activeOccupation?.id;
        if (!occupationId && diningOptionObj?.id) {
            try {
                const occ = await occupationService.getByTable(diningOptionObj.id, tableNumber);
                occupationId = occ?.id;
                setActiveOccupation(occ || null);
            } catch (_) { /* ignore */ }
        }
        if (occupationId) {
            try {
                await occupationService.requestBill(occupationId);
                setSuccess(t('bill_requested') || 'Bill requested');
                setTimeout(() => setSuccess(''), 2000);
            } catch (err) {
                console.error('requestBill error', err);
                setError(t('failed_to_request_bill') || 'Failed to request bill');
            }
        }
    };

    // Mark the current dine-in table as served
    const handleMarkServed = async () => {
        const diningOptionObj = diningOptionObjects[selectedDiningOption];
        let occupationId = activeOccupation?.id;
        if (!occupationId && diningOptionObj?.id) {
            try {
                const occ = await occupationService.getByTable(diningOptionObj.id, tableNumber);
                occupationId = occ?.id;
                setActiveOccupation(occ || null);
            } catch (_) { /* ignore */ }
        }
        if (occupationId) {
            try {
                await occupationService.markServed(occupationId);
                setSuccess(t('table_marked_served') || 'Table marked as served');
                setTimeout(() => setSuccess(''), 2000);
            } catch (err) {
                console.error('markServed error', err);
                setError(t('failed_to_mark_served') || 'Failed to mark as served');
            }
        }
    };

    // Helper function to check if dining option requires service charge
    const requiresServiceCharge = (diningOption) => {
        const optionObj = diningOptionObjects?.[diningOption];
        return optionObj?.service_charge_applied === true;
    };

    // Helper function to recalculate order total with service charge
    const recalculateOrderWithServiceCharge = (order) => {
        if (!order || !order.products) return order;

        const shouldHaveServiceCharge = requiresServiceCharge(order.diningOption);

        if (shouldHaveServiceCharge && !order.addServiceCharge) {
            // Recalculate with service charge
            const subtotal = order.products.reduce((total, product) => {
                const productTotal = (parseFloat(product.selling_price) || 0) * (parseInt(product.quantity) || 1);
                const addonTotal = product.add_ons
                    ? product.add_ons.reduce((sum, addon) => sum + ((parseFloat(addon.price) || 0) * (parseInt(product.quantity) || 1)), 0)
                    : 0;
                return total + productTotal + addonTotal;
            }, 0);

            const serviceCharge = subtotal * (tenantConfig?.service_charge_rate || 0.12);
            const newTotal = Math.max(0, subtotal + serviceCharge - (order.discount || 0) - (order.promoDiscountAmount || 0));

            return {
                ...order,
                addServiceCharge: true,
                serviceCharge: serviceCharge,
                subtotal: subtotal,
                total: newTotal
            };
        }

        return order;
    };

    const {
        pendingOrders, setPendingOrders,
        pendingOrderIds, setPendingOrderIds,
        printedOrders, setPrintedOrders,
        refreshTableOrders,
    } = usePendingOrders(isBusinessDay, businessDaySalesDate, recalculateOrderWithServiceCharge);

    useEffect(() => {
        const { ensureOrderPersistence } = require('../../utils/OrderPersistence');
        ensureOrderPersistence();
    }, []);

    useEffect(() => {
        const loadPrimaryColor = async () => {
            if (getTenantSetting) {
                const color = await getTenantSetting('theme.primary_color', '#0ea5e9');
                setPrimaryColor(color);
            }
        };
        loadPrimaryColor();
    }, [getTenantSetting]);

    // Fetch initial data on mount
    useEffect(() => {
        const fetchInitialData = async () => {
            if (!tenantConfig || configLoaded) return;

            try {
                setLoading(true);

                // Load employees and business day state
                const [employeeData, businessDayState] = await Promise.all([
                    employeeService.getAllEmployees(),
                    tenantStorage.getBusinessDayState()
                ]);

                // Process data in single batch
                const allActiveCashiers = employeeData.filter(emp =>
                    !!emp.is_cashier && emp.status?.toLowerCase() === 'active'
                );

                setCashiers(allActiveCashiers);

                // Load products, categories, and bulk stock via hook
                const { productData } = await loadCatalog();

                // Feed the modifier catalog too (Task 17) — without this the
                // builder's modifierCatalog never leaves its empty useState
                // default and every product falls back to the legacy flat
                // add-on path. Routes online/offline the same way loadCatalog
                // does (modifierService.getCatalog() branches internally).
                modifierService.getCatalog()
                    .then(setModifierCatalog)
                    .catch((err) => console.error('Failed to load modifier catalog:', err));

                setIsBusinessDay(businessDayState.isBusinessDay || false);
                setBusinessDaySalesDate(businessDayState.salesDate || '');

                // Fetch payment methods from API
                try {
                    const methods = await paymentMethodService.getAll();
                    setPaymentMethods(methods.filter(m => m.is_active).map(m => m.name));
                } catch (e) {
                    console.error('Failed to fetch payment methods, falling back to config:', e);
                    setPaymentMethods(tenantConfig.payment_methods || []);
                }

                // Fetch dining options from API
                await loadDiningOptions();

                setConfigLoaded(true);

                // NEW: Load printing preference and receipt translation settings
                try {
                    const [printingPref, translationsPref, receiptLangPref] = await Promise.all([
                        tenantStorage.getState('printingEnabled'),
                        tenantStorage.getState('receiptTranslationsEnabled'),
                        tenantStorage.getState('receiptTranslationLanguage')
                    ]);

                    setPrintingEnabled(printingPref !== false); // Default to true if not set
                    setReceiptTranslationsEnabled(translationsPref !== false); // Default to true if not set

                    // Determine receipt translation language
                    let receiptLang = receiptLangPref;
                    if (!receiptLang && tenantConfig?.languages && tenantConfig.languages.length > 1) {
                        // Default to the second language in tenant config
                        receiptLang = tenantConfig.languages.find(lang => lang !== 'en') || tenantConfig.languages[1];
                    }
                    setReceiptTranslationLanguage(receiptLang || '');

                    setPrintingConfigLoaded(true);
                    setTranslationsConfigLoaded(true);

                    // No shipping host has a silent-print bridge to probe for
                    // a default OS printer, so printerWarning is never set —
                    // kept as dead state only because the Alert below still
                    // renders it if it ever is.
                } catch (error) {
                    console.error('Error loading preferences:', error);
                    setPrintingEnabled(true); // Default to enabled
                    setReceiptTranslationsEnabled(true); // Default to enabled

                    // Set default receipt translation language from tenant config
                    if (tenantConfig?.languages && tenantConfig.languages.length > 1) {
                        const defaultLang = tenantConfig.languages.find(lang => lang !== 'en') || tenantConfig.languages[1];
                        setReceiptTranslationLanguage(defaultLang || '');
                    }

                    setPrintingConfigLoaded(true);
                    setTranslationsConfigLoaded(true);
                }

                await loadTransactions();
                setLoading(false);
            } catch (err) {
                setError(`Failed to load data: ${err.message}`);
                setLoading(false);
            }
        };

        fetchInitialData();
    }, [tenantConfig]); // Only depend on tenantConfig

    // Fetch active occupations for table color coding + bill request detection
    useEffect(() => {
        const fetchOccs = async () => {
            try {
                const occs = await occupationService.listActive();
                setActiveOccupations(occs);
                const newNotifs = [];
                const activeIds = new Set(occs.map(o => o.id));
                occs.forEach(occ => {
                    if (occ.bill_requested_at && !seenBillRequests.current.has(occ.id)) {
                        seenBillRequests.current.add(occ.id);
                        newNotifs.push({
                            id: occ.id,
                            tableNumber: occ.table_number,
                            diningOptionId: occ.dining_option_id,
                            guestName: occ.guest_name || occ.guest?.name,
                            time: new Date(),
                        });
                    }
                    if (!occ.bill_requested_at && seenBillRequests.current.has(occ.id)) {
                        seenBillRequests.current.delete(occ.id);
                    }
                });
                // Remove notifications for occupations that no longer exist (cleared/paid)
                const staleIds = [...seenBillRequests.current].filter(id => !activeIds.has(id));
                staleIds.forEach(id => seenBillRequests.current.delete(id));
                if (staleIds.length > 0) {
                    setBillNotifications(prev => prev.filter(n => !staleIds.includes(n.id)));
                }
                if (newNotifs.length > 0) {
                    setBillNotifications(prev => [...newNotifs, ...prev]);
                }
            } catch {}
        };
        fetchOccs();
        // Poll every ~20s, skip while hidden, jitter to stagger terminals.
        const interval = setInterval(() => {
            if (document.visibilityState !== 'visible') return;
            fetchOccs();
        }, 20000 + Math.floor(Math.random() * 2000));
        return () => clearInterval(interval);
    }, []);

    // Load transactions for the selected date
    const loadTransactions = async () => {
        try {
            const isBusinessDay = await tenantStorage.getState('isBusinessDay');
            const salesDate = await tenantStorage.getState('salesDate');
            if (isBusinessDay && salesDate) {
                try {
                    const transactionData = await transactionService.getTransactionsAfterStartOfDay(salesDate);
                    setTransactions(transactionData);
                } catch (err) {
                    console.error('Failed to load by sales_date with time filter:', err);
                    setError(t('failed_to_load_transactions'));
                }
            } else {
                setTransactions([]);
            }
        } catch (err) {
            setError('Failed to load transactions');
            console.error(err);
        }
    };

    const reloadTransactions = async () => {
        await loadTransactions();
    };

    // Helper function to get service charge info for display
    const getServiceChargeInfo = (diningOption) => {
        const optionObj = diningOptionObjects?.[diningOption];
        const willHaveServiceCharge = optionObj?.service_charge_applied === true;
        const rate = Math.round((tenantConfig?.service_charge_rate || 0.12) * 100);

        return {
            willApply: willHaveServiceCharge,
            rate: rate,
            text: willHaveServiceCharge ? t('service_charge_will_be_applied', { rate }) : t('no_service_charge')
        };
    };

    const handleReceiptTranslationsToggle = async (enabled) => {
        try {
            setReceiptTranslationsEnabled(enabled);
            await tenantStorage.setState('receiptTranslationsEnabled', enabled);

            // Also load/save the translation language setting from localStorage
            if (enabled) {
                const storedLang = localStorage.getItem('receiptTranslationLanguage');
                if (storedLang && storedLang !== receiptTranslationLanguage) {
                    setReceiptTranslationLanguage(storedLang);
                    await tenantStorage.setState('receiptTranslationLanguage', storedLang);
                }
            }

        } catch (error) {
            console.error('Error saving receipt translations preference:', error);
            setError(t('failed_to_save_receipt_preference'));
        }
    };

    // Order editing protection functions
    const checkEditingConflict = (actionType, actionData = null) => {
        if (!editingOrderKey) {
            return false; // No conflict, not editing
        }

        // Allow certain actions on the same order being edited
        if (actionType === 'editSameOrder' && actionData === editingOrderKey) {
            return false; // Allow editing the same order
        }

        // Show protection modal for conflicting actions
        setPendingAction(actionType);
        setPendingActionData(actionData);
        setEditingProtectionModalOpen(true);
        return true; // Conflict detected
    };

    const handleSaveAndContinue = () => {
        // Save current editing changes
        saveEditedOrder();

        // Execute the pending action
        executePendingAction();

        // Close modal
        setEditingProtectionModalOpen(false);
        setPendingAction(null);
        setPendingActionData(null);
    };

    const handleDiscardAndContinue = () => {
        // Discard current editing changes
        cancelEditingOrder();

        // Execute the pending action
        executePendingAction();

        // Close modal
        setEditingProtectionModalOpen(false);
        setPendingAction(null);
        setPendingActionData(null);
    };

    const handleCancelAction = () => {
        // Just close modal and continue editing
        setEditingProtectionModalOpen(false);
        setPendingAction(null);
        setPendingActionData(null);
    };

    const executePendingAction = () => {
        if (!pendingAction) return;

        switch (pendingAction) {
            case 'changeDiningOption':
                setSelectedDiningOption(pendingActionData);
                setTableNumber('');
                break;

            case 'changeTableNumber':
                setTableNumber(pendingActionData);
                break;

            case 'editDifferentOrder':
                // Call the original startEditPendingOrder without protection
                // TODO: startEditPendingOrderDirect is undefined (orphaned conflict-action path) — needs the editing-conflict owner to wire up or remove.
                // eslint-disable-next-line no-undef
                startEditPendingOrderDirect(pendingActionData);
                break;

            case 'submitSale':
                // Call the original handleSubmitSale without protection
                handleSubmitSaleDirect();
                break;

            case 'clearBasket':
                setSelectedProducts([]);
                break;

            default:
                console.warn('Unknown pending action:', pendingAction);
        }
    };


    const handleSubmitSaleDirect = async () => {
        await handleSubmitSale();
    };

    // Select a dining option from the pill row — reuses the exact logic the
    // dining-option cards used (editing-conflict guard + no-table daily-order preview).
    const handleDiningPillSelect = async (option) => {
        if (checkEditingConflict('changeDiningOption', option)) return;
        setSelectedDiningOption(option);
        setSelectedCategory(null);
        setSelectedSubcategory(null);
        setTableNumberConfirmed(false);
        // Preview next daily order number for no-table dining options (don't increment yet)
        const optConfig = availableTableNumbers[option];
        if (optConfig == null || optConfig === '') {
            const salesDate = await tenantStorage.getState('salesDate') || businessDaySalesDate;
            const counterKey = `daily_order_${option}_${salesDate}`;
            const prevCount = parseInt(await tenantStorage.getState(counterKey)) || 0;
            setTableNumber(`#${String(prevCount + 1).padStart(3, '0')}`);
        } else {
            setTableNumber('');
        }
    };

    // Add a product to the cart — nudges to pick a dining option first when none is set
    // (the flat flow lets you browse without one; adding still requires it).
    const handleAddProduct = (product) => {
        if (!selectedDiningOption) {
            setError(t('select_dining_option_first') || 'Select a dining option first');
            setTimeout(() => setError(''), 2500);
            setPillHighlight(true);
            setTimeout(() => setPillHighlight(false), 1200);
            return;
        }
        handleProductWithAddons(product);
    };

    // NEW: Handle printing toggle change
    const handlePrintingToggle = async (enabled) => {
        try {
            setPrintingEnabled(enabled);
            await tenantStorage.setState('printingEnabled', enabled);
        } catch (error) {
            console.error('Error saving printing preference:', error);
            setError(t('failed_to_save_printing_preference'));
        }
    };

    // createItemsWithAddons is imported from the shared cashier transaction
    // flow — see the import block at the top of this file. The private copy
    // that used to live here predated modifier groups: it emitted no
    // line_id/parent_line_id/group_id, so a nested modifier was posted as a
    // sibling of the option it belongs to, and it consolidated rows on
    // product_code|||notes, which merged two of the same product ordered with
    // different modifiers into one line.

    const linkInventoryToTransaction = async (products, transactionId) => {
        try {
            // Update pending order transactions if this is a pending order
            if (currentConfirmOrder) {
                const tableKey = currentConfirmOrder;
                await api.post('/inventory/update-pending-order-transactions', {
                    table_key: tableKey,
                    transaction_id: transactionId
                });
            } else {
                // For immediate sales, create new inventory transaction records
                // Convert products to proper TransactionItem format
                const transactionItems = products.flatMap(product => {
                    const items = [{
                        product_code: product.product_code,
                        product_name: product.name,
                        category: product.category,
                        quantity: product.quantity,
                        unit_price: parseFloat(product.selling_price),
                        total_amount: parseFloat(product.selling_price) * product.quantity,
                        notes: product.notes || ''
                    }];

                    // Add addon items if they exist
                    if (product.add_ons && product.add_ons.length > 0) {
                        product.add_ons.forEach(addon => {
                            items.push({
                                product_code: addon.product_code || `ADD${String(addon.id).padStart(3, '0')}`,
                                product_name: `${addon.name} - Add on`,
                                category: product.category,
                                quantity: product.quantity,
                                unit_price: parseFloat(addon.price),
                                total_amount: parseFloat(addon.price) * product.quantity,
                                notes: `Add-on for: ${product.name}`
                            });
                        });
                    }

                    return items;
                });

                await api.post('/inventory/link-to-transaction', {
                    transaction_id: transactionId,
                    items: transactionItems,
                    dining_option: selectedDiningOption,
                    created_by: cashierName
                });
            }
        } catch (error) {
            console.error('Error linking inventory to transaction:', error);
            throw error;
        }
    };

    // Submit sale - REVIEWED AND IMPROVED VERSION
    const handleSubmitSale = async () => {
        try {
            // Step 0: Check if business day has started
            if (!isBusinessDay || !businessDaySalesDate) {
                setError(t('sales_not_allowed_start_business_day'));
                return;
            }

            // Step 1: Validate form inputs
            if (!cashierName) {
                setError(t('please_select_cashier'));
                return;
            }

            if (selectedProducts.length === 0) {
                setError(t('please_add_at_least_one_product'));
                return;
            }

            if (!selectedDiningOption) {
                setError(t('please_select_dining_option'));
                return;
            }

            // Step 2: Validate table number requirements
            const diningConfig = availableTableNumbers[selectedDiningOption];
            const requiresTableNumber = diningConfig != null && diningConfig !== "";

            if (requiresTableNumber && !tableNumber) {
                setError(t('please_enter_table_number', { option: selectedDiningOption }));
                return;
            }

            // Step 3: Charge ALWAYS means an immediate sale now — kitchen
            // ticket, payment and close in one action (client request
            // 2026-08-26). A table/tent order used to be forced down the
            // pending-order path here, which is why Charge appeared to do
            // nothing for Dine In. Holding is now its own explicit button
            // (handleHoldOrder below), for the events case where a waiter
            // takes the order and it is settled later.
            await handleImmediateSaleCreation();

        } catch (error) {
            console.error('Error in handleSubmitSale:', error);
            setError(t('failed_to_process_sale', { error: error.message || t('unknown_error') }));
        }
    };

    // Hold — the pending-order flow, reached by its own button rather than
    // by Charge silently rerouting. Guards mirror handleSubmitSale's.
    const handleHoldOrder = async () => {
        if (selectedProducts.length === 0) {
            setError(t('please_add_at_least_one_product'));
            return;
        }
        if (!selectedDiningOption) {
            setError(t('please_select_dining_option'));
            return;
        }
        const diningConfig = availableTableNumbers[selectedDiningOption];
        const requiresTableNumber = diningConfig != null && diningConfig !== "";
        if (requiresTableNumber && !tableNumber) {
            setError(t('please_enter_table_number', { option: selectedDiningOption }));
            return;
        }
        await handlePendingOrderCreation();
    };

    // Helper function for pending order creation
    const handlePendingOrderCreation = async () => {
        const tableKey = `${selectedDiningOption}-${tableNumber}`;

        // Step 1: Check if table is already in use (and not being edited)
        if (pendingOrders[tableKey] && editingOrderKey !== tableKey) {
            setError(t('table_already_has_pending_order', { option: selectedDiningOption, number: tableNumber }));
            return;
        }

        // Step 2: DEDUCT STOCK IMMEDIATELY when creating pending order
        try {
            await deductStockForPendingOrder(selectedProducts, selectedDiningOption, tableNumber, cashierName);
        } catch (error) {
            console.error('Error deducting stock for pending order:', error);
            setError(t('failed_to_deduct_stock'));
            return;
        }

        // Step 3: Ensure the order is not marked as printed initially
        setPrintedOrders(prev => {
            const updated = { ...prev };
            delete updated[tableKey];
            return updated;
        });

        // Step 4: Create pending order object
        const _totalsForPending = handleCalculateTotals();
        const pendingOrder = {
            time: format(new Date(), 'HH:mm:ss'),
            date: format(new Date(), 'yyyy-MM-dd'),
            sales_date: await tenantStorage.getState('salesDate') || businessDaySalesDate,
            cashier: cashierName,
            diningOption: selectedDiningOption,
            tableNumber: tableNumber,
            products: [...selectedProducts],
            subtotal: _totalsForPending.subtotal,
            discount: discountAmount,
            serviceCharge: _totalsForPending.serviceCharge,
            total: Math.max(0, _totalsForPending.total - promoDiscountAmount),
            notes: notes,
            paymentMethod: '',
            addServiceCharge: addServiceCharge,
            addTax: addTax,
            tax: _totalsForPending.tax,
            is_submitted: false,
            is_printed: false,
            // Promotion fields
            appliedPromoId: appliedPromo?.promotion?.promotion_id || null,
            promoDiscountAmount: promoDiscountAmount,
        };

        try {
            const currentSalesDate = await tenantStorage.getState('salesDate');
            const response = await tenantStorage.saveOrder(tableKey, pendingOrder, false, false, null, currentSalesDate);

            const orderId = response.order_id;

            if (!orderId) {
                throw new Error('Failed to get order ID from tenant storage');
            }

            // Step 5: Update local state
            setPendingOrders(prev => ({
                ...prev,
                [tableKey]: { ...pendingOrder, order_id: orderId }
            }));

            setPendingOrderIds(prev => ({
                ...prev,
                [tableKey]: orderId
            }));

            // Step 6: Print kitchen/bar receipts and create tracking
            const orderWithId = { ...pendingOrder, order_id: orderId };
            await printPendingOrderReceipts(orderWithId);

            // Step 7: Reset UI state
            resetAllFormState();
            setMobileCartOpen(false);

            setSuccess(t('order_added_to_pending_success'));
            setTimeout(() => setSuccess(''), 2000);

            if (activeOccupation?.id) {
                try { await occupationService.markFirstOrder(activeOccupation.id); } catch {}
            }

            // Step 8: Refresh stock calculations to reflect the deduction
            await refreshBulkStock();

        } catch (error) {
            console.error('Failed to create pending order:', error);
            setError(t('failed_to_save_pending_order'));

            // Clean up any partial state updates
            setPendingOrders(prev => {
                const cleaned = { ...prev };
                delete cleaned[tableKey];
                return cleaned;
            });

            setPendingOrderIds(prev => {
                const cleaned = { ...prev };
                delete cleaned[tableKey];
                return cleaned;
            });

            // REVERT STOCK DEDUCTION if order creation failed
            try {
                await revertStockDeduction(selectedProducts, cashierName);
            } catch (revertError) {
                console.error('Failed to revert stock deduction:', revertError);
            }

            // Refresh stock calculations after revert
            await refreshBulkStock();
        }
    };

    // Helper function for immediate sale creation
    const handleImmediateSaleCreation = async () => {
        try {
            // Step 1: Calculate totals
            const { subtotal, serviceCharge, tax, total } = handleCalculateTotals();

            // Step 2: Create transaction items with add-ons
            const items = createItemsWithAddons(selectedProducts);

            // Step 3: Get current sales date
            const currentSalesDate = await tenantStorage.getState('salesDate') || businessDaySalesDate;

            // Step 4: Create transaction object
            const transaction = {
                transaction_id: '', // Will be generated by backend
                date: format(new Date(), 'yyyy-MM-dd'),
                time: format(new Date(), 'HH:mm:ss'),
                sales_date: currentSalesDate,
                staff_name: cashierName,
                payment_method: '', // Will be set when payment method is selected
                dining_option: selectedDiningOption,
                table_number: tableNumber || null, // Daily order number for no-table options
                service_charge: addServiceCharge,
                discount_amount: discountAmount,
                total_amount: subtotal,
                final_amount: Math.max(0, total - promoDiscountAmount),
                tax: tax,
                notes: notes,
                items: items,
                products: selectedProducts, // Keep reference for receipt printing
                tenant_id: tenantId,
                // Promotion fields
                applied_promo_id: appliedPromo?.promotion?.promotion_id || null,
                promo_discount_amount: promoDiscountAmount,
            };

            // Step 5: Set pending transaction and open payment modal
            setPendingTransaction(transaction);
            setPaymentConfirmModalOpen(true);

        } catch (error) {
            console.error('Failed to create immediate sale:', error);
            setError('Failed to prepare sale transaction. Please try again.');
        }
    };

    // Helper function to reset form state (wraps hook's resetFormState + page-specific resets)
    const resetAllFormState = () => {
        resetFormState(paymentMethods, setSelectedPaymentMethod);
        setSelectedCategory(null);
        setSelectedSubcategory(null);
        setSelectedDiningOption(null);
        setTableNumber('');
        setTableNumberConfirmed(false);
        resetPromo();
    };
    // Enhanced version that creates order tracking for PENDING orders
    const printPendingOrderReceipts = async (order) => {
        try {
            setSuccess('Preparing order for kitchen/bar...');

            const tenantInfo = {
                brand_name: brandName,
                currency,
                printable_categories: printableCategories,
                receipt_header: tenantConfig?.receipt_header,
                receipt_footer: tenantConfig?.receipt_footer,
                receipt_logo: tenantConfig?.receipt_logo ? (tenantConfig.receipt_logo.startsWith('http') ? tenantConfig.receipt_logo : `${window.location.origin}/${tenantConfig.receipt_logo}`) : null
            };

            const receipts = [];
            const trackingRecords = []; // Store tracking records created

            // Create receipts and tracking records for each printable category that has items
            for (const category of printableCategories) {
                const categoryItems = order.products.filter(p => p.category === category);
                if (categoryItems.length > 0) {
                    // Create receipt
                    const categoryData = {
                        date: format(new Date(), 'yyyy-MM-dd'),
                        time: format(new Date(), 'HH:mm:ss'),
                        cashier: cashierName,
                        paymentMethod: selectedPaymentMethod,
                        diningOption: selectedDiningOption,
                        tableNumber: tableNumber,
                        products: categoryItems,
                        notes: notes
                    };
                    const receiptT = createReceiptTranslator(receiptTranslationLanguage);

                    receipts.push({
                        title: `${category.toUpperCase()} ORDER`,
                        html: generateReceiptHTML(categoryData, `${category.toUpperCase()} ORDER`, true, tenantInfo, receiptTranslationsEnabled, receiptTranslationLanguage, receiptT, dynamicTranslations, translationsLoaded)

                    });

                    // Create order tracking record - THIS IS THE KEY CHANGE
                    try {
                        const trackingData = {
                            transaction_id: null,
                            order_id: order.order_id,
                            category: category,
                            printed_categories: true,
                            items: categoryItems.map(item => ({
                                name: item.name,
                                quantity: item.quantity,
                                notes: item.notes || null,
                                add_ons: item.add_ons || []
                            })),
                            table_info: {
                                dining_option: selectedDiningOption,
                                table_number: tableNumber,
                                staff_name: cashierName || 'Unknown'
                            },
                            total_items_count: categoryItems.reduce((sum, item) => sum + item.quantity, 0),
                            notes: notes || null
                        };
                        const trackingResult = await orderTrackingService.createOrderTracking(trackingData);
                        trackingRecords.push(trackingResult);
                        // Dispatch cross-tab event to notify OrderViewPage about new order
                        const eventData = {
                            type: 'orderSubmitted',
                            category: category,
                            orderType: 'new_order',
                            orderId: order.order_id,
                            timestamp: Date.now()
                        };

                        // Use localStorage to trigger cross-tab events
                        localStorage.setItem('orderEvent', JSON.stringify(eventData));
                        localStorage.removeItem('orderEvent');
                    } catch (trackingError) {
                        console.error(`Failed to create tracking for ${category}:`, trackingError);
                        // Don't fail the entire process if tracking fails
                    }
                }
            }

            // Handle items not in printable categories
            const otherItems = order.products.filter(p =>
                !printableCategories.includes(p.category)
            );
            if (otherItems.length > 0) {
                const otherData = {
                    date: format(new Date(), 'yyyy-MM-dd'),
                    time: format(new Date(), 'HH:mm:ss'),
                    cashier: cashierName,
                    paymentMethod: selectedPaymentMethod,
                    diningOption: selectedDiningOption,
                    tableNumber: tableNumber,
                    products: otherItems,
                    notes: notes
                };
                const receiptT = createReceiptTranslator(receiptTranslationLanguage);

                receipts.push({
                    title: "OTHER ITEMS",
                    html: generateReceiptHTML(otherData, "OTHER ITEMS", true, tenantInfo, receiptTranslationsEnabled, receiptTranslationLanguage, receiptT, dynamicTranslations, translationsLoaded)
                });
            }

            // Print all receipts if any exist
            if (receipts.length > 0 && printingEnabled) {
                const allReceiptsHTML = receipts.map(receipt =>
                    `<div class="print-page">${receipt.html}</div>`
                ).join('');

                printWithIframe(allReceiptsHTML, `${brandName.toUpperCase()} Orders`, tenantConfig?.printing?.method);
            }

            if (trackingRecords.length > 0) {
                setSuccess(`Order sent to ${trackingRecords.length} station(s) and receipts printed. Staff can now start preparing!`);
            }

        } catch (err) {
            console.error('Error during printing and tracking:', err);
            setError('There was an error processing the order. Please try again.');
        }
    };

    const processTransactionWithPayment = async (paymentMethod) => {
        if (!paymentMethod) {
            return;
        }

        if (!pendingTransaction) {
            console.error('No pending transaction found');
            setError(t('no_transaction_to_process'));
            return;
        }

        const transactionWithPayment = {
            ...pendingTransaction,
            payment_method: paymentMethod,
            tenant_id: tenantId
        };

        // Set payment amounts based on method
        if (paymentMethod === 'Cash') {
            transactionWithPayment.cash_amount = transactionWithPayment.final_amount;
            transactionWithPayment.card_amount = 0;
        } else if (paymentMethod === 'Card') {
            transactionWithPayment.cash_amount = 0;
            transactionWithPayment.card_amount = transactionWithPayment.final_amount;
        } else if (paymentMethod === 'Split Payment') {
            // Use the split functions from SplitBillManager ref - show split type selection first
            if (splitBillRef.current) {
                splitBillRef.current.showSplitTypeModal();
            }
            setPaymentConfirmModalOpen(false);
            setSelectedModalPaymentMethod('Split Payment');
            return;
        } else {
            transactionWithPayment.cash_amount = 0;
            transactionWithPayment.card_amount = 0;
        }

        let transactionResult = null;
        let orderWasSubmitted = false;

        try {
            setLoading(true);
            transactionResult = await transactionService.createTransaction(transactionWithPayment);

            if (!transactionResult || !transactionResult.transaction_id) {
                throw new Error('Transaction creation failed - no transaction ID returned');
            }

            // transactionWithPayment was built from pendingTransaction, whose
            // transaction_id is always '' (generated backend-side). Merge the
            // real id back in now so anything reading transactionWithPayment
            // below — the tenantInfo the print path receives included — has it.
            // Same fix as transactionFlow.js (the native cashier surface);
            // without it Epson direct print is inert on Electron/browser POS,
            // which route here via App.js.
            transactionWithPayment.transaction_id = transactionResult.transaction_id;

            // Increment daily order counter for no-table dining options after successful transaction
            if (tableNumber && tableNumber.startsWith('#')) {
                const salesDate = await tenantStorage.getState('salesDate') || businessDaySalesDate;
                const counterKey = `daily_order_${selectedDiningOption}_${salesDate}`;
                const current = parseInt(await tenantStorage.getState(counterKey)) || 0;
                await tenantStorage.setState(counterKey, current + 1);
            }

            // Step 2: Create inventory transactions with transaction_id
            try {
                // Link existing inventory deductions to this transaction
                await linkInventoryToTransaction(selectedProducts, transactionResult.transaction_id);
            } catch (inventoryError) {
                console.error('Error linking inventory to transaction:', inventoryError);
                // Continue - transaction was successful
            }

            // NEW STEP 2.5: Update tracking records with transaction ID
            try {
                // For immediate sales, create tracking records with real transaction_id
                if (!currentConfirmOrder) {
                    // This is an immediate sale


                    for (const category of printableCategories) {
                        const categoryItems = selectedProducts.filter(p => p.category === category);
                        if (categoryItems.length > 0) {
                            const trackingData = {
                                transaction_id: transactionResult.transaction_id,
                                category: category,
                                printed_categories: true,
                                items: categoryItems.map(item => ({
                                    name: item.name,
                                    quantity: item.quantity,
                                    notes: item.notes || null,
                                    add_ons: item.add_ons || []
                                })),
                                table_info: {
                                    dining_option: selectedDiningOption,
                                    // Same fix as the native surface's
                                    // dispatchKitchenTickets: hardcoded null left a
                                    // takeaway kitchen ticket with no identifier while
                                    // the customer's receipt for the same sale read
                                    // "Order: #019". Carries the daily counter for a
                                    // no-table option and the table for a table one;
                                    // the renderers split them on the leading "#".
                                    table_number: tableNumber || null,
                                    staff_name: cashierName || 'Unknown'
                                },
                                total_items_count: categoryItems.reduce((sum, item) => sum + item.quantity, 0),
                                notes: notes || null
                            };

                            await orderTrackingService.createOrderTracking(trackingData);
                        }
                    }
                } else {
                    // This is a pending order - update existing tracking records with transaction_id
                    const orderToSubmit = pendingOrders[currentConfirmOrder];
                    if (orderToSubmit && orderToSubmit.order_id) {
                        try {
                            // Use the correct API endpoint for updating tracking records
                            await api.put(`/orders/tracking/update-by-order/${orderToSubmit.order_id}?transaction_id=${transactionResult.transaction_id}`);
                        } catch (updateError) {
                            console.error('Failed to update tracking with transaction_id:', updateError);
                            // Don't fail the transaction if tracking update fails - log and continue
                        }
                    }
                }
            } catch (trackingError) {
                console.error('Error handling order tracking:', trackingError);
                // Don't fail the transaction if tracking fails
            }

            // Step 3: Prepare receipt data
            const receiptData = {
                date: format(new Date(), 'yyyy-MM-dd'),
                time: format(new Date(), 'HH:mm:ss'),
                cashier: cashierName,
                paymentMethod: paymentMethod,
                diningOption: selectedDiningOption,
                tableNumber: tableNumber,
                products: selectedProducts,
                subtotal: transactionWithPayment.total_amount,
                discount: transactionWithPayment.discount_amount,
                promoDiscount: transactionWithPayment.promo_discount_amount || 0,
                promoName: appliedPromo?.promotion?.name || '',
                serviceCharge: transactionWithPayment.service_charge ?
                    (transactionWithPayment.total_amount * (tenantConfig?.service_charge_rate || 0.12)) : 0,
                tax: transactionWithPayment.tax || 0,
                total: transactionWithPayment.final_amount,
                notes: transactionWithPayment.notes
            };

            const tenantInfo = {
                brand_name: brandName,
                currency,
                printable_categories: printableCategories,
                printing: tenantConfig?.printing,
                receipt_header: tenantConfig?.receipt_header,
                receipt_footer: tenantConfig?.receipt_footer,
                receipt_logo: tenantConfig?.receipt_logo ? (tenantConfig.receipt_logo.startsWith('http') ? tenantConfig.receipt_logo : `${window.location.origin}/${tenantConfig.receipt_logo}`) : null,
                // Epson direct print (PrintingUtils.js) needs the real transaction id
                // to fetch its render-and-address payload from the backend.
                transaction_id: transactionWithPayment.transaction_id
            };

            // Step 4: Print receipts
            const receiptT = createReceiptTranslator(receiptTranslationLanguage);
            printReceipts(receiptData, selectedProducts, !!currentConfirmOrder, tenantInfo, printingEnabled, receiptTranslationsEnabled, receiptTranslationLanguage, receiptT, dynamicTranslations, translationsLoaded);

            // Step 5: Mark order as submitted ONLY after everything succeeds
            if (currentConfirmOrder && pendingOrders[currentConfirmOrder]) {
                // Double-check the order still exists and is unsubmitted
                const orderToSubmit = pendingOrders[currentConfirmOrder];
                if (orderToSubmit && !orderToSubmit.is_submitted) {
                    const orderId = pendingOrderIds[currentConfirmOrder];

                    try {
                        await tenantStorage.markOrderSubmitted(currentConfirmOrder, true, orderId);
                        orderWasSubmitted = true;

                        // Remove from local state only after successful submission
                        setPendingOrders(prev => {
                            const newOrders = { ...prev };
                            delete newOrders[currentConfirmOrder];
                            return newOrders;
                        });

                        setPrintedOrders(prev => {
                            const newPrintedOrders = { ...prev };
                            delete newPrintedOrders[currentConfirmOrder];
                            return newPrintedOrders;
                        });

                        setCurrentConfirmOrder(null);
                    } catch (submissionError) {
                        console.error('Failed to mark order as submitted:', submissionError);
                        // Don't throw here - transaction was successful, just log the error
                        setError('Transaction successful but failed to update order status. Please check manually.');
                    }
                } else {
                    console.warn('Order not found or already submitted:', currentConfirmOrder);
                }
            }

            // Step 6: Close modals and reset state
            setPaymentConfirmModalOpen(false);
            setPendingTransaction(null);
            setSelectedModalPaymentMethod(null);

            // Step 6b: a charged TABLE/TENT order stays visible as a
            // hand-over queue until the cashier taps Clear (client request
            // 2026-08-26). Best-effort: the money is already banked, so a
            // failure here must not surface as a payment error — worst case
            // the order is missing from the queue but still on the KDS.
            try {
                const diningConfig = availableTableNumbers[selectedDiningOption];
                const isTableOrder = diningConfig != null && diningConfig !== "";
                if (isTableOrder && tableNumber && !currentConfirmOrder) {
                    await createPaidOrder({
                        selectedDiningOption,
                        tableNumber,
                        setPendingOrders,
                        setPendingOrderIds,
                        setPrintedOrders,
                        selectedProducts,
                        cashierName,
                        businessDaySalesDate,
                        totals: handleCalculateTotals(),
                        discountAmount,
                        addServiceCharge,
                        addTax,
                        notes,
                        appliedPromo,
                        promoDiscountAmount,
                        // transactionResult, NOT pendingTransaction — the
                        // latter's transaction_id is always '' (the backend
                        // mints it, merged into transactionWithPayment at
                        // line ~1261). Reading it there left every paid
                        // order unvoidable.
                        transactionId: transactionResult.transaction_id,
                        paymentMethod,
                    });
                }
            } catch (paidOrderError) {
                console.error('Failed to record paid order for hand-over:', paidOrderError);
            }

            // Step 7: Reload transactions and reset form
            await loadTransactions();

            if (currentConfirmOrder) {
                const order = pendingOrders[currentConfirmOrder];
                if (order?.products) {
                    // Get unique categories from order products
                    const orderCategories = [...new Set(order.products.map(p => p.category))];
                    orderCategories.forEach(cat => {
                        window.dispatchEvent(new CustomEvent('orderSubmitted', {
                            detail: { category: cat }
                        }));
                    });
                }
            }

            // Reset form state
            setSelectedProducts([]);
            setNotes('');
            setDiscountType('amount');
            setDiscountPercentage(0);
            setDiscountAmount(0);
            setPayableAmount(0);

            setSelectedCategory(null);
            setSelectedSubcategory(null);
            setSelectedDiningOption(null);
            setTableNumber('');
            setTableNumberConfirmed(false);
            setAddServiceCharge(false);
            setAddTax(false);
            setDiscountMode('direct');
            setMobileCartOpen(false);

            setSelectedModalPaymentMethod(null);
            resetPromo();

            setSuccess(t('sale_recorded_successfully'));
            setLoading(false);

            // Clear success message
            setTimeout(() => {
                setSuccess('');
            }, 3000);

        } catch (err) {
            console.error('Error in processTransactionWithPayment:', err);

            const selectionProblems = selectionProblemsFrom(err);

            // If we managed to mark the order as submitted but something else failed,
            // we should NOT revert the submission (transaction was successful)
            if (orderWasSubmitted && transactionResult) {
                setError(t('transaction_completed_refresh_error'));
            } else if (selectionProblems) {
                // Server-side modifier validation rejected this before writing
                // anything. Name the unanswered groups so the cashier can fix
                // the cart rather than staring at an opaque 400 mid-service.
                // Same handling as transactionFlow.js (the native surface).
                setError(`${t('selection_incomplete')} ${selectionProblems.join(' • ')}`);
            } else {
                // Complete failure - transaction may not have been created.
                // Prefer what the SERVER said over axios's "Request failed
                // with status code 400", which is unactionable at a till.
                // Same handling as transactionFlow.js (the native surface).
                setError(`${t('failed_to_record_sale')} ${serverMessageFrom(err) || err.message || t('unknown_error')}`);
            }

            setLoading(false);
            setPaymentConfirmModalOpen(false);
            setSelectedModalPaymentMethod(null);

            // If transaction failed completely, keep the pending transaction for retry
            if (!transactionResult) {
                // Don't clear pendingTransaction so user can retry
            }
        }
    };


    const cancelEditingOrder = () => {
        resetFormState();
        setEditingOrderKey(null);
        setSelectedDiningOption(null);
        setSelectedCategory(null);
        setSelectedSubcategory(null);
        setTableNumber('');
        setTableNumberConfirmed(false);
        resetPromo();
    };

    const saveEditedOrder = async () => {
        if (!editingOrderKey) return;

        try {
            const originalOrder = { ...pendingOrders[editingOrderKey] };

            const updatedOrder = {
                time: pendingOrders[editingOrderKey].time,
                date: format(new Date(), 'yyyy-MM-dd'),
                cashier: cashierName,
                diningOption: selectedDiningOption,
                tableNumber: tableNumber,
                products: [...selectedProducts],
                subtotal: handleCalculateTotals().subtotal,
                discount: discountAmount,
                serviceCharge: handleCalculateTotals().serviceCharge,
                total: Math.max(0, handleCalculateTotals().total - promoDiscountAmount),
                notes: notes,
                paymentMethod: selectedPaymentMethod,
                addServiceCharge: addServiceCharge,
                addTax: addTax,
                tax: handleCalculateTotals().tax,
                appliedPromoId: appliedPromo?.promotion?.promotion_id || null,
                promoDiscountAmount: promoDiscountAmount,
            };

            const currentSalesDate = await tenantStorage.getState('salesDate');
            await tenantStorage.saveOrder(editingOrderKey, updatedOrder, false, false, null, currentSalesDate);
            setPendingOrders(prev => ({
                ...prev,
                [editingOrderKey]: updatedOrder
            }));

            // Anything this edit ADDED has to come off the shelf now — see
            // the same call in pos-app/surfaces/cashier/lib/pendingOrderFlow.js.
            try {
                await deductStockForAddedItems(
                    originalOrder.products || [], selectedProducts,
                    selectedDiningOption, tableNumber, cashierName,
                );
            } catch (stockError) {
                console.error('Failed to deduct stock for added items:', stockError);
            }

            printUpdatedOrderReceipts(originalOrder, updatedOrder);

            const orderId = pendingOrderIds[editingOrderKey];
            if (orderId) {
                await updateOrderTrackingRecords(
                    originalOrder.products || [],
                    selectedProducts,
                    orderId,
                    originalOrder,
                    tenantConfig,
                    selectedDiningOption,
                    editingOrderKey,
                    username,
                    products,
                    true,
                    { printableCategories }
                );
            }

            setSuccess(t('order_updated_successfully'));
            setTimeout(() => setSuccess(''), 1000);
        } catch (err) {
            console.error('Error saving edited order:', err);
            setError(t('failed_to_update_order', { error: err.message }));
        } finally {
            // Always clear editing state, even on error
            setSelectedProducts([]);
            setNotes('');
            setDiscountType('amount');
            setDiscountPercentage(0);
            setDiscountAmount(0);
            setPayableAmount(0);

            setEditingOrderKey(null);
            setSelectedCategory(null);
            setSelectedSubcategory(null);
            setTableNumber('');
            setTableNumberConfirmed(false);
            setSelectedDiningOption(null);
            setAddServiceCharge(false);
            setAddTax(false);
            setDiscountMode('direct');
            setMobileCartOpen(false);
        }
    };

    // Helper function for printing updated order receipts
    const printUpdatedOrderReceipts = (originalOrder, updatedOrder) => {
        const originalProducts = originalOrder.products || [];
        const updatedProducts = updatedOrder.products || [];
        const newProducts = [];

        updatedProducts.forEach(updatedProduct => {
            // Find matching original product (same product_code and same add-ons)
            const originalProduct = originalProducts.find(p => {
                if (p.product_code !== updatedProduct.product_code) return false;

                // Compare add-ons
                const originalAddons = (p.add_ons || []).map(addon => addon.product_code || addon.id).sort();
                const updatedAddons = (updatedProduct.add_ons || []).map(addon => addon.product_code || addon.id).sort();

                // Check if addon arrays are the same
                if (originalAddons.length !== updatedAddons.length) return false;

                return originalAddons.every((id, index) => id === updatedAddons[index]);
            });

            if (!originalProduct) {
                // This is a completely new product or new add-on combination
                newProducts.push({ ...updatedProduct });
            } else if (updatedProduct.quantity > originalProduct.quantity) {
                // Only print if quantity increased - show the additional amount
                const additionalQuantity = updatedProduct.quantity - originalProduct.quantity;

                newProducts.push({
                    ...updatedProduct,
                    quantity: additionalQuantity  // Show only the additional amount
                });
            }
            // Note: We don't print anything if quantity decreased or stayed the same
        });

        // Only proceed if there are actually new products/quantities
        if (newProducts.length === 0) {
            return;
        }

        const receiptData = {
            date: updatedOrder.date,
            time: format(new Date(), 'HH:mm:ss'),
            cashier: updatedOrder.cashier,
            paymentMethod: updatedOrder.paymentMethod,
            diningOption: updatedOrder.diningOption,
            tableNumber: updatedOrder.tableNumber,
            products: newProducts,
            notes: updatedOrder.notes
        };

        const receipts = [];

        printableCategories.forEach(category => {
            const categoryItems = newProducts.filter(p => p.category === category);
            if (categoryItems.length > 0) {
                const categoryData = { ...receiptData, products: categoryItems };
                const receiptT = createReceiptTranslator(receiptTranslationLanguage);

                receipts.push({
                    html: generateReceiptHTML(categoryData, `${category.toUpperCase()} ORDER - UPDATED`, true, tenantConfig, receiptTranslationsEnabled, receiptTranslationLanguage, receiptT, dynamicTranslations, translationsLoaded),
                    title: `${category.toUpperCase()} ORDER - UPDATED`
                });
            }
        });

        const otherItems = newProducts.filter(p => !printableCategories.includes(p.category));
        if (otherItems.length > 0) {
            const otherData = { ...receiptData, products: otherItems };
            const receiptT = createReceiptTranslator(receiptTranslationLanguage);

            receipts.push({
                html: generateReceiptHTML(otherData, "OTHER ITEMS - UPDATED", true, tenantConfig, receiptTranslationsEnabled, receiptTranslationLanguage, receiptT, dynamicTranslations, translationsLoaded),
                title: "OTHER ITEMS - UPDATED"
            });
        }

        if (receipts.length === 0) {
            return;
        }

        const allReceiptsHTML = receipts.map(receipt =>
            `<div class="print-page">${receipt.html}</div>`
        ).join('');

        printWithIframe(allReceiptsHTML, `${brandName.toUpperCase()} Updated Orders`, tenantConfig?.printing?.method);
    };

    // Resolves the typed draft into a discount amount and parks it for
    // approval. Nothing reaches the builder here — see onDiscountApproved.
    const requestDiscountApply = () => {
        const { subtotal, serviceCharge } = handleCalculateTotals();
        const typed = parseFloat(discountDraft) || 0;
        let amount = 0;
        if (discountMode === 'direct') amount = typed;
        else if (discountMode === 'percentage') amount = parseFloat((subtotal * typed / 100).toFixed(2));
        else amount = parseFloat((subtotal + serviceCharge - typed).toFixed(2));
        if (!amount) return;
        setPendingDiscount({ amount, typed });
    };

    const onDiscountApproved = () => {
        if (!pendingDiscount) return;
        setDiscountAmount(pendingDiscount.amount);
        if (discountMode === 'percentage') setDiscountPercentage(pendingDiscount.typed);
        if (discountMode === 'payable') setPayableAmount(pendingDiscount.typed);
        setPendingDiscount(null);
    };

    // Clearing a discount is deliberately ungated — it cannot undercharge.
    const clearDiscount = () => {
        setDiscountAmount(0);
        setDiscountPercentage(0);
        setPayableAmount(0);
        setDiscountDraft('');
    };

    // Hand-over for a paid order — the cashier gives the food to the
    // customer. Ungated: handing food over cannot lose money.
    const handleClearPaidOrder = async (tableKey) => {
        try {
            await clearPaidOrder(tableKey, {
                setPendingOrders, setPrintedOrders, setPendingOrderIds,
                activeOccupation: null,
            });
            setSuccess(t('order_cleared') || 'Order handed over');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error('Failed to clear paid order:', err);
            setError(t('failed_to_delete_order'));
        }
    };

    // Deleting a pending order needs a manager (client request 2026-08-26).
    // Gate FIRST, reason second — asking a cashier to justify a deletion that
    // is then refused is the wrong order. handleDeleteOrder only records which
    // order is pending approval; openDeleteReasonModal is the original body.
    const handleDeleteOrder = async (tableKey) => {
        if (!pendingOrders[tableKey]) return;
        setPendingDeleteKey(tableKey);
    };

    const openDeleteReasonModal = async (tableKey, approvedBy) => {
        const order = pendingOrders[tableKey];
        if (!order) return;

        deletionConfirm.confirmDeletion({
            onDelete: async (reason) => {
                try {
                    setLoading(true);

                    // A PAID order has money against it. There is no refund
                    // concept in this system — the reversal is the existing
                    // transaction void, which snapshots the sale into `voids`,
                    // restores inventory and hard-deletes it so the day's
                    // takings reconcile against the drawer. `approvedBy` is
                    // the manager from the gate, not the cashier who asked.
                    if (order.paid) {
                        await deletePaidOrderAction(tableKey, reason, {
                            order, approvedBy, cashierName,
                            setPendingOrders, setPrintedOrders, setPendingOrderIds,
                            activeOccupation: null,
                        });
                        setSuccess(t('order_deleted_successfully'));
                        setLoading(false);
                        setTimeout(() => setSuccess(''), 3000);
                        return;
                    }

                    // First, log the deletion in the backend (creates void records)
                    try {
                        await orderTrackingService.deletePendingOrder(tableKey, reason, order, cashierName);
                    } catch (backendError) {
                        console.error('Failed to log order deletion in backend:', backendError);
                        if (backendError.response?.status === 403) {
                            setError(t('insufficient_permissions_delete_order'));
                            setLoading(false);
                            throw backendError; // Stop execution
                        }
                        // Continue with frontend deletion even if backend logging fails
                    }

                    // Then delete from tenant storage (frontend)
                    await tenantStorage.deleteOrder(tableKey);

                    // Update local state
                    setPendingOrders(prev => {
                        const newOrders = { ...prev };
                        delete newOrders[tableKey];
                        return newOrders;
                    });

                    setPrintedOrders(prev => {
                        const newPrintedOrders = { ...prev };
                        delete newPrintedOrders[tableKey];
                        return newPrintedOrders;
                    });

                    setPendingOrderIds(prev => {
                        const newOrderIds = { ...prev };
                        delete newOrderIds[tableKey];
                        return newOrderIds;
                    });

                    setSuccess(t('order_deleted_successfully'));
                    setLoading(false);

                    setTimeout(() => setSuccess(''), 3000);
                } catch (err) {
                    setLoading(false);
                    if (err.response?.status === 403) {
                        setError(t('insufficient_permissions_delete_order'));
                    } else {
                        setError(t('failed_to_delete_order'));
                    }
                    console.error('Error deleting order:', err);
                    throw err; // Re-throw so the modal knows there was an error
                }
            },
            title: t('delete_order'),
            message: t('confirm_delete_order_for', { diningOption: order.diningOption, tableNumber: order.tableNumber }),
            itemName: `${order.diningOption} ${order.tableNumber} - ${currency}${(parseFloat(order.total) || 0).toFixed(2)}`,
            requireReason: true
        });
    };

    // Table-service dining options still require a table before the grid is usable.
    // This is the only surviving slice of the old step machine — it gates the
    // table-selection UI (reached via the dining pill / Floor Plan), nothing else.
    const needsTableSelection = useMemo(() => {
        if (!selectedDiningOption) return false;
        const tableConfig = availableTableNumbers[selectedDiningOption];
        if (Array.isArray(tableConfig) && !tableNumber) return true;
        if (tableConfig === 'random_number' && !tableNumberConfirmed) return true;
        return false;
    }, [selectedDiningOption, tableNumber, tableNumberConfirmed, availableTableNumbers]);

    // Products shown in the grid. With a category selected, reuse the hook's
    // category/subcategory/search-filtered list; the "All" view derives the same
    // way from the already-fetched `products` (no extra fetch).
    const gridProducts = useMemo(() => {
        if (selectedCategory) return filteredProducts;
        let filtered = [...products].sort((a, b) => a.name.localeCompare(b.name));
        if (productSearch.trim()) {
            const term = productSearch.toLowerCase().trim();
            filtered = filtered.filter(p =>
                p.name.toLowerCase().includes(term) ||
                p.product_code.toLowerCase().includes(term) ||
                (p.subcategory && p.subcategory.toLowerCase().includes(term))
            );
        }
        return filtered;
    }, [selectedCategory, filteredProducts, products, productSearch]);

    // With the category-level "All" chip gone there has to be a landing
    // category, or the till opens on an empty grid with no obvious next tap.
    // Only fires when nothing is selected, so it never overrides a real choice.
    useEffect(() => {
        if (!selectedCategory && categories.length > 0) {
            setSelectedCategory(categories[0]);
        }
    }, [selectedCategory, categories, setSelectedCategory]);

    // While reordering, render the arrangement the manager is building rather
    // than waiting for a refetch — the list must not jump under their thumb.
    const displayProducts = useMemo(() => {
        if (!reordering || !localOrder) return gridProducts;
        const byCode = {};
        gridProducts.forEach((p) => { byCode[p.product_code] = p; });
        return localOrder.map((code) => byCode[code]).filter(Boolean);
    }, [reordering, localOrder, gridProducts]);

    // One call carrying the whole new order — a half-applied reorder would
    // leave the menu in an arrangement nobody chose.
    const moveProduct = async (productCode, delta) => {
        const codes = (localOrder || displayProducts.map((p) => p.product_code)).slice();
        const from = codes.indexOf(productCode);
        const to = from + delta;
        if (from < 0 || to < 0 || to >= codes.length) return;
        codes.splice(to, 0, codes.splice(from, 1)[0]);
        setLocalOrder(codes);
        try {
            await catalogueOrderService.reorder('products', codes);
        } catch (err) {
            console.error('Failed to persist menu order:', err);
        }
    };

    // Chip-row sibling of moveProduct. delta is -1/+1 along a horizontal
    // scroller, so the controls read left/right rather than up/down.
    const moveCategory = async (categoryName, delta) => {
        const names = (localCategoryOrder || categories).slice();
        const from = names.indexOf(categoryName);
        const to = from + delta;
        if (from < 0 || to < 0 || to >= names.length) return;
        names.splice(to, 0, names.splice(from, 1)[0]);
        setLocalCategoryOrder(names);
        // Keyed by id — a name is not unique enough to renumber on. If any id
        // is missing the whole call is skipped rather than sending undefined,
        // which would 404 and lose the entire reorder.
        const ids = names.map((name) => categoryObjects[name]?.id)
            .filter((id) => id !== undefined && id !== null);
        if (ids.length !== names.length) {
            console.error('Skipping category reorder: some categories have no id');
            return;
        }
        try {
            await catalogueOrderService.reorder('categories', ids);
        } catch (err) {
            console.error('Failed to persist category order:', err);
        }
    };

    // Show the arrangement being built rather than waiting for a refetch.
    const displayCategories = useMemo(() => {
        if (!reordering || !localCategoryOrder) return categories;
        const known = new Set(categories);
        return localCategoryOrder.filter((name) => known.has(name));
    }, [reordering, localCategoryOrder, categories]);

    // Independent of service_charge_applied: "applies + locked" is the
    // combination the client asked for and one flag cannot express it.
    const serviceChargeToggleVisible =
        diningOptionObjects?.[selectedDiningOption]?.service_charge_toggle_visible !== false;

    // Loading spinner
    if (loading) {
        return <LoadingSpinner />;
    }


    // Helper: derive business day display for top bar (and the More menu's sale-info section).
    // Exposes the raw parts (name / sale number / date) alongside the combined label so both
    // the compact mobile header line and the More menu's full read-out can reuse them.
    const businessDayInfo = (() => {
        if (!isBusinessDay || !businessDaySalesDate) return null;
        const name = cashierName || username || 'Staff';
        const sessionId = activeSession?.session_id || '';
        try {
            const parts = (businessDaySalesDate || '').split(' ');
            const d = new Date(parts[0] + 'T00:00:00');
            const datePart = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
            const timePart = parts[1] ? parts[1].substring(0, 5) : '';
            const dateStr = timePart ? `${datePart} · ${timePart}` : datePart;
            const label = sessionId
                ? `${name} · ${sessionId} · ${dateStr}`
                : `Active · ${dateStr}`;
            return { name, sessionId, dateStr, label };
        } catch {
            return { name, sessionId, dateStr: businessDaySalesDate, label: `Active · ${businessDaySalesDate}` };
        }
    })();
    const businessDayLabel = businessDayInfo?.label || null;

    if (sessionLoading) return null;

    if (showSoD && !activeSession) {
        const now = new Date();
        const salesDate = `${now.toISOString().split('T')[0]} ${now.toTimeString().split(' ')[0]}`;
        const name = cashierName || username || 'Staff';
        const empId = employeeId || `ACC_${username}`;
        return (
            <StartOfDayScreen
                employeeId={empId}
                employeeName={name}
                salesDate={salesDate}
                onSessionStarted={handleSessionStarted}
                onBack={() => navigate('/functions')}
            />
        );
    }

    if (showEoD && activeSession) {
        const pendingList = Object.values(pendingOrders || {}).filter(o => o.status !== 'completed');
        return (
            <EndOfDayFlow
                session={activeSession}
                pendingOrders={pendingList}
                onSessionClosed={handleSessionClosed}
                onCancel={() => setShowEoD(false)}
            />
        );
    }

    const renderCartPanel = () => (
        <>

                        {/* Order header */}
                        <div className="flex-shrink-0 px-4 pt-3 pb-2 border-b border-gray-100">
                            <div className="flex justify-between items-center">
                                <div className="text-xs font-bold uppercase tracking-wide text-gray-500">
                                    {selectedDiningOption ? (
                                        <span>
                                            {getEntityTranslation(diningOptionObjects[selectedDiningOption], language, translationDetails) || translateDynamic(selectedDiningOption, t)}
                                            {tableNumber && <span> · {tableNumber}</span>}
                                        </span>
                                    ) : (
                                        <span className="text-gray-400">{t('order') || 'Order'}</span>
                                    )}
                                </div>
                                <span className="text-xs text-gray-400">{cashierName}</span>
                            </div>
                            {editingOrderKey && (
                                <div className="mt-1 text-xs text-amber-600 font-medium bg-amber-50 rounded px-2 py-0.5">
                                    ✎ {t('editing_order') || 'Editing order'}
                                </div>
                            )}
                        </div>

                        {/* Order items list */}
                        <div className="flex-1 overflow-y-auto px-4 py-2">
                            {selectedProducts.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-center text-gray-400">
                                    <svg className="w-10 h-10 mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                    <p className="text-sm">{t('no_products_selected') || 'No items yet'}</p>
                                    {!selectedDiningOption && (
                                        <p className="text-xs mt-1">{t('select_dining_option_to_begin') || 'Select a dining option to begin'}</p>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-1">
                                    {selectedProducts.map(product => (
                                        <div key={product.uniqueId || `${product.product_code}-${product.notes || ''}`} className="border-b border-gray-50 pb-2 last:border-0">
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1 min-w-0 mr-2">
                                                    <div className="text-sm font-medium text-gray-900 truncate">
                                                        {getProductName(product, language, translationDetails) ?? getTransliteration(product.name, language, t)}
                                                    </div>
                                                    <div className="text-xs text-gray-400">{currency}{parseFloat(product.selling_price).toFixed(2)}</div>
                                                    {product.add_ons && product.add_ons.length > 0 && (
                                                        <div className="mt-0.5 space-y-0.5">
                                                            {product.add_ons.map((addon, idx) => (
                                                                <div key={addon.product_code || addon.id || idx} className="text-xs text-gray-500 flex justify-between">
                                                                    <span>+ {addon.name}</span>
                                                                    <span>{currency}{parseFloat(addon.price).toFixed(2)}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <input
                                                        type="text"
                                                        placeholder={t('add_notes_for_item')}
                                                        value={product.notes || ''}
                                                        onChange={(e) => handleUpdateProductNotes(product.uniqueId || product.product_code, e.target.value)}
                                                        className="mt-1 w-full text-xs border-b border-dashed border-gray-200 bg-transparent focus:outline-none focus:border-primary-400 placeholder:text-gray-300"
                                                    />
                                                </div>
                                                <div className="flex items-center gap-1 flex-shrink-0">
                                                    <button
                                                        onClick={() => handleUpdateProductQuantity(product.uniqueId || product.product_code, Math.max(1, product.quantity - 1))}
                                                        className="w-6 h-6 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded text-sm font-medium"
                                                    >−</button>
                                                    <span className="w-6 text-center text-sm font-semibold">{product.quantity}</span>
                                                    <button
                                                        onClick={() => handleUpdateProductQuantity(product.uniqueId || product.product_code, product.quantity + 1)}
                                                        className="w-6 h-6 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded text-sm font-medium"
                                                    >+</button>
                                                    <button
                                                        onClick={() => handleRemoveProduct(product.uniqueId || product.product_code)}
                                                        className="ml-1 text-red-400 hover:text-red-600"
                                                    >
                                                        <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* ── EXPAND: Discount panel */}
                        {showDiscountPanel && (
                            <div className="flex-shrink-0 px-3 pb-2">
                                <div className="p-2 border rounded-lg space-y-2" style={{ borderColor: primaryColor + '66', backgroundColor: primaryColor + '0d' }}>
                                    <div className="flex gap-1">
                                        {[
                                            { key: 'direct', label: t('fixed_discount') || 'Fixed Amount' },
                                            { key: 'percentage', label: t('percentage') || 'Percentage' },
                                            { key: 'payable', label: t('payable_amount') || 'Payable Amount' },
                                        ].map(opt => (
                                            <button
                                                key={opt.key}
                                                onClick={() => { setDiscountMode(opt.key); setDiscountAmount(0); setDiscountPercentage(0); setPayableAmount(0); setDiscountDraft(''); setPendingDiscount(null); }}
                                                className="flex-1 py-1 rounded text-xs font-medium border transition-colors"
                                                style={discountMode === opt.key
                                                    ? { backgroundColor: primaryColor, color: '#fff', borderColor: primaryColor }
                                                    : { backgroundColor: '#fff', color: '#374151', borderColor: '#d1d5db' }}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {/* All three inputs write to the local
                                            `discountDraft` only — nothing reaches
                                            the builder (and so the totals) until a
                                            manager approves via Apply. */}
                                        {discountMode === 'direct' && (<>
                                            <span className="text-xs text-gray-500 flex-shrink-0">{currency}</span>
                                            <input type="number" min="0" step="0.01" placeholder="0.00"
                                                data-testid="discount-input"
                                                value={discountDraft}
                                                onChange={(e) => setDiscountDraft(e.target.value)}
                                                className="flex-1 text-xs border rounded px-2 py-1 bg-white focus:outline-none"
                                                style={{ borderColor: primaryColor + '80' }}
                                                autoFocus />
                                        </>)}
                                        {discountMode === 'percentage' && (<>
                                            <input type="number" min="0" max="100" step="1" placeholder="0"
                                                data-testid="discount-input"
                                                value={discountDraft}
                                                onChange={(e) => setDiscountDraft(e.target.value)}
                                                className="flex-1 text-xs border rounded px-2 py-1 bg-white focus:outline-none"
                                                style={{ borderColor: primaryColor + '80' }}
                                                autoFocus />
                                            <span className="text-xs text-gray-500 flex-shrink-0">%</span>
                                        </>)}
                                        {discountMode === 'payable' && (<>
                                            <span className="text-xs text-gray-500 flex-shrink-0">{currency}</span>
                                            <input type="number" step="0.01" placeholder="0.00"
                                                data-testid="discount-input"
                                                value={discountDraft}
                                                onChange={(e) => setDiscountDraft(e.target.value)}
                                                className="flex-1 text-xs border rounded px-2 py-1 bg-white focus:outline-none"
                                                style={{ borderColor: primaryColor + '80' }}
                                                autoFocus />
                                        </>)}
                                        <button
                                            type="button"
                                            data-testid="discount-apply"
                                            onClick={requestDiscountApply}
                                            className="flex-shrink-0 rounded px-3 py-1 text-xs font-bold text-white"
                                            style={{ backgroundColor: primaryColor }}
                                        >
                                            {t('apply') || 'Apply'}
                                        </button>
                                        {discountAmount !== 0 && (
                                            <button onClick={clearDiscount}
                                                className="text-xs text-red-400 hover:text-red-600 flex-shrink-0">✕</button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── EXPAND: Notes panel */}
                        {showNotesPanel && (
                            <div className="flex-shrink-0 px-3 pb-2">
                                <textarea
                                    placeholder={t('add_order_notes') || 'Add order notes...'}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    rows={2}
                                    className="w-full text-xs border rounded px-2 py-1.5 resize-none bg-white placeholder:text-gray-400 focus:outline-none"
                                    style={{ borderColor: primaryColor + '80' }}
                                    autoFocus
                                />
                            </div>
                        )}

                        {/* Cart action row: Discount / Notes / Clear */}
                        <div className="flex flex-shrink-0 gap-2 px-4 pb-1">
                            <button
                                onClick={() => { setShowDiscountPanel(v => !v); setShowNotesPanel(false); }}
                                className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-gray-300 bg-gray-50 py-2 text-xs font-bold text-gray-700"
                                style={(showDiscountPanel || discountAmount !== 0 || discountPercentage > 0 || discountMode === 'payable')
                                    ? { backgroundColor: primaryColor, borderColor: primaryColor, color: '#fff' }
                                    : undefined}
                            >
                                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v14a2 2 0 01-2 2z"/></svg>
                                {t('discount') || 'Discount'}
                                {discountAmount !== 0 && <span className="opacity-90">{discountAmount > 0 ? '−' : '+'}{currency}{Math.abs(parseFloat(discountAmount)).toFixed(2)}</span>}
                            </button>
                            <button
                                onClick={() => { setShowNotesPanel(v => !v); setShowDiscountPanel(false); }}
                                className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-gray-300 bg-gray-50 py-2 text-xs font-bold text-gray-700"
                                style={(showNotesPanel || notes) ? { backgroundColor: primaryColor, borderColor: primaryColor, color: '#fff' } : undefined}
                            >
                                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                                {t('notes') || 'Notes'}{notes ? ' •' : ''}
                            </button>
                            <button
                                onClick={() => {
                                    setSelectedProducts([]);
                                    setDiscountAmount(0);
                                    setDiscountPercentage(0);
                                    setDiscountMode('direct');
                                    setPayableAmount(0);
                                    setNotes('');
                                    setAddServiceCharge(false);
                                    setAddTax(false);
                                    setShowDiscountPanel(false);
                                    setShowNotesPanel(false);
                                    resetPromo();
                                }}
                                disabled={selectedProducts.length === 0 && !notes && !discountAmount}
                                className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-red-200 bg-red-50 py-2 text-xs font-bold text-red-600 disabled:opacity-40"
                            >
                                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                                {t('clear_basket') || 'Clear All'}
                            </button>
                        </div>

                        {/* Totals + CHARGE */}
                        <div className="flex-shrink-0 border-t border-gray-200 px-4 pt-3 pb-3">
                            {selectedProducts.length > 0 && (() => {
                                const { subtotal, serviceCharge, tax, total } = handleCalculateTotals();
                                const displayTotal = Math.max(0, total - promoDiscountAmount);
                                return (
                                    <div className="space-y-1 mb-3">
                                        <div className="flex justify-between text-xs text-gray-500">
                                            <span>{t('subtotal')}</span>
                                            <span>{currency}{subtotal.toFixed(2)}</span>
                                        </div>
                                        {addServiceCharge && (
                                            <div className="flex justify-between text-xs text-gray-500">
                                                <span>{t('service_charge_with_percent', { percent: Math.round((tenantConfig?.service_charge_rate || 0.12) * 100) })}</span>
                                                <span>{currency}{serviceCharge.toFixed(2)}</span>
                                            </div>
                                        )}
                                        {addTax && tax > 0 && (
                                            <div className="flex justify-between text-xs text-gray-500">
                                                <span>{t('tax_with_percent', { percent: Math.round((tenantConfig?.tax_rate || 0) * 100) })}</span>
                                                <span>{currency}{tax.toFixed(2)}</span>
                                            </div>
                                        )}
                                        {discountAmount !== 0 && (
                                            <div className={`flex justify-between text-xs ${discountAmount > 0 ? 'text-red-500' : 'text-green-600'}`}>
                                                <span>{discountAmount > 0 ? t('discount') : t('markup') || 'Markup'}</span>
                                                <span>{discountAmount > 0 ? '−' : '+'} {currency}{Math.abs(parseFloat(discountAmount)).toFixed(2)}</span>
                                            </div>
                                        )}
                                        {/* Promo discount line */}
                                        {appliedPromo && (
                                            <div className="flex items-center justify-between text-xs text-green-700 bg-green-50 rounded px-1.5 py-1">
                                                <span className="flex items-center gap-1">
                                                    🏷️ <span className="font-medium">{appliedPromo.promotion.name}</span>
                                                    <button
                                                        onClick={removePromo}
                                                        className="ml-1 text-gray-400 hover:text-red-500 leading-none"
                                                        title={t('remove_promo')}
                                                    >✕</button>
                                                </span>
                                                <span className="font-semibold">−{currency}{promoDiscountAmount.toFixed(2)}</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between text-sm font-bold pt-1 border-t border-gray-100">
                                            <span>{t('total')}</span>
                                            <span>{currency}{displayTotal.toFixed(2)}</span>
                                        </div>
                                    </div>
                                );
                            })()}
                            {/* Charge + Hold side by side for a table/tent
                                order (client request 2026-08-26). Charge =
                                kitchen + pay + close; Hold = pending order,
                                settled later. Editing keeps UPDATE ORDER as
                                the single action. */}
                            {(() => {
                                const diningConfig = availableTableNumbers[selectedDiningOption];
                                const showHold = diningConfig != null && diningConfig !== "" && !editingOrderKey;
                                return showHold && selectedProducts.length > 0 ? (
                                    <button
                                        data-testid="hold-btn"
                                        onClick={handleHoldOrder}
                                        className="mb-2 w-full rounded-lg border-2 border-primary-600 bg-white py-3 text-sm font-bold tracking-wide text-primary-600 transition-colors hover:bg-primary-50"
                                    >
                                        {t('hold_order') || 'Hold order'}
                                    </button>
                                ) : null;
                            })()}
                            <button
                                data-testid="charge-btn"
                                onClick={selectedProducts.length > 0 ? (editingOrderKey ? saveEditedOrder : handleSubmitSale) : undefined}
                                disabled={selectedProducts.length === 0}
                                className={`w-full rounded-lg py-3 text-sm font-bold tracking-wide transition-colors ${
                                    selectedProducts.length > 0
                                        ? 'bg-primary-600 hover:bg-primary-700 text-white cursor-pointer'
                                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                }`}
                            >
                                {editingOrderKey
                                    ? (selectedProducts.length > 0
                                        ? `\u2705 ${t('update_order') || 'UPDATE ORDER'} \u2014 ${currency}${Math.max(0, handleCalculateTotals().total - promoDiscountAmount).toFixed(2)}`
                                        : `\u2705 ${t('update_order') || 'UPDATE ORDER'}`)
                                    : (selectedProducts.length > 0
                                        ? `\uD83D\uDCB3 ${t('charge') || 'CHARGE'} \u2014 ${currency}${Math.max(0, handleCalculateTotals().total - promoDiscountAmount).toFixed(2)}`
                                        : `\uD83D\uDCB3 ${t('charge') || 'CHARGE'}`)
                                }
                            </button>
                            {/* Hidden ENTIRELY when the dining option locks the
                                service charge — a bare label with no checkbox
                                reads as a broken control. Nothing is lost: the
                                totals block above already shows
                                "Service Charge (30%)" with its amount, so the
                                customer-facing total stays explained. (The
                                native cart differs: there the label IS the
                                totals row, so it keeps its text.) */}
                            {serviceChargeToggleVisible && (
                            <div className="mt-2">
                                <label className="flex items-center gap-1.5 cursor-pointer select-none text-xs text-gray-500">
                                    <input
                                        type="checkbox"
                                        checked={addServiceCharge}
                                        onChange={(e) => setAddServiceCharge(e.target.checked)}
                                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                    />
                                    {t('service_charge') || 'Service charge'}
                                </label>
                            </div>
                            )}

                            {/* Dine-in table actions: Request Bill + Mark Served */}
                        </div>

        </>
    );

    return (
        <div className="flex flex-col h-full">

            {/* ── SLIM TOP BAR */}
            <div className={isMobile ? 'flex-shrink-0 flex items-center justify-between min-h-[40px] py-1 bg-primary-600 px-4' : 'flex-shrink-0 flex items-center justify-between h-10 bg-primary-600 px-4'}>
                <div className="flex items-center gap-3">
                    <img
                        src={(() => {
                            const configLogo = tenantConfig?.brand_logo || '/aqana-logo.png?v=6';
                            if (host.usesHashRouter) {
                                const clean = configLogo.startsWith('/') ? configLogo.substring(1) : configLogo;
                                return `./${clean}`;
                            }
                            return configLogo.startsWith('/') || configLogo.startsWith('http') ? configLogo : `/${configLogo}`;
                        })()}
                        alt={brandName}
                        className="h-6 w-auto"
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <span className="text-white text-sm font-semibold hidden sm:block">{brandName}</span>
                </div>

                {isMobile ? (
                    // Compact: screen title + one truncated context line (sale number · employee)
                    <div className="flex flex-col min-w-0 flex-1 px-3 leading-tight">
                        <span className="text-white text-xs font-bold truncate">{t('cashier_sales') || 'Cashier Sales'}</span>
                        <span className="flex items-center gap-1 min-w-0">
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${businessDayInfo ? 'bg-green-400 shadow-[0_0_4px_#4ade80]' : 'bg-red-400'}`} />
                            <span className={`text-[10px] truncate min-w-0 ${businessDayInfo ? 'text-green-200' : 'text-red-200'}`}>
                                {businessDayInfo
                                    ? `#${businessDayInfo.sessionId || '—'} · ${businessDayInfo.name}`
                                    : (t('no_active_business_day') || 'No Business Day')}
                            </span>
                        </span>
                    </div>
                ) : (
                    <div className="flex items-center gap-2 text-xs">
                        {businessDayLabel ? (
                            <span className="flex items-center gap-1.5 text-green-300">
                                <span className="w-2 h-2 rounded-full bg-green-400 shadow-[0_0_4px_#4ade80]" />
                                {businessDayLabel}
                            </span>
                        ) : (
                            <span className="flex items-center gap-1.5 text-red-300">
                                <span className="w-2 h-2 rounded-full bg-red-400" />
                                {t('no_active_business_day') || 'No Business Day'}
                            </span>
                        )}
                    </div>
                )}

                <div className={isMobile ? 'flex items-center gap-1' : 'flex items-center gap-2'}>
                    <StaffAllowanceManager
                        cashierName={cashierName}
                        currency={currency}
                        tenantConfig={tenantConfig}
                        brandName={brandName}
                        setError={setError}
                        setSuccess={setSuccess}
                        printingEnabled={printingEnabled}
                        receiptTranslationsEnabled={receiptTranslationsEnabled}
                        receiptTranslationLanguage={receiptTranslationLanguage}
                        hideButton
                        externalOpen={openStaffAllowance}
                    />
                    {/* Orders button with live badge */}
                    <button
                        onClick={() => setOrdersPanelOpen(!ordersPanelOpen)}
                        style={{
                            padding: '4px 12px', borderRadius: 6,
                            fontSize: 11, fontWeight: 700,
                            background: '#2563eb', color: '#fff',
                            border: 'none', cursor: 'pointer',
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            letterSpacing: 0.2, lineHeight: 1,
                            transition: 'background 150ms ease',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#1d4ed8'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = '#2563eb'; }}
                    >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
                            <path d="M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        {isMobile
                            ? (Object.keys(pendingOrders).length > 0 ? Object.keys(pendingOrders).length : null)
                            : (Object.keys(pendingOrders).length > 0
                                ? `${Object.keys(pendingOrders).length} ${t('active') || 'Active'}`
                                : (t('orders') || 'Orders')
                            )
                        }
                    </button>
                    {/* More dropdown (Staff Allowance, Table Service, End Session) */}
                    <div style={{ position: 'relative' }}>
                        <button
                            onClick={() => setShowMoreMenu(!showMoreMenu)}
                            style={{
                                padding: '4px 12px', borderRadius: 6,
                                border: '1px solid rgba(255,255,255,0.3)',
                                background: 'rgba(255,255,255,0.15)', color: 'white',
                                fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                fontFamily: "'Plus Jakarta Sans', sans-serif",
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                lineHeight: 1,
                            }}
                        >
                            {t('more') || 'More'} <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
                        </button>
                        {showMoreMenu && (
                            <>
                                <div onClick={() => setShowMoreMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 48 }} />
                                <div style={{
                                    position: 'absolute', top: '100%', right: 0, marginTop: 4,
                                    background: 'white', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                                    border: '1px solid #e2e8f0', minWidth: 220, zIndex: 50, overflow: 'hidden',
                                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                                }}>
                                    {/* Sale info — mobile only, read-only (the header shows a truncated version) */}
                                    {isMobile && (
                                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                                            <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-600">
                                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${businessDayInfo ? 'bg-green-400' : 'bg-red-400'}`} />
                                                <span>
                                                    {businessDayInfo
                                                        ? (businessDayInfo.sessionId ? `${businessDayInfo.name} · ${businessDayInfo.sessionId}` : businessDayInfo.name)
                                                        : (t('no_active_business_day') || 'No Business Day')}
                                                </span>
                                            </div>
                                            {businessDayInfo && (
                                                <div className="text-xs text-gray-400 mt-1 pl-3.5">{businessDayInfo.dateStr}</div>
                                            )}
                                        </div>
                                    )}
                                    <button
                                        onClick={() => { setOpenStaffAllowance(true); setTimeout(() => setOpenStaffAllowance(false), 100); setShowMoreMenu(false); }}
                                        style={{
                                            width: '100%', padding: '10px 16px', border: 'none', background: 'none',
                                            textAlign: 'left', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#212529',
                                            borderBottom: '1px solid #f1f5f9',
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                                    >
                                        {t('staff_allowance') || 'Staff Allowance'}
                                    </button>
                                    <button
                                        onClick={() => { navigate('/functions/table-service'); setShowMoreMenu(false); }}
                                        style={{
                                            width: '100%', padding: '10px 16px', border: 'none', background: 'none',
                                            textAlign: 'left', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#212529',
                                            borderBottom: '1px solid #f1f5f9',
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                                    >
                                        {t('table_service') || 'Table Service'}
                                    </button>
                                    {/* The same pane the native till mounts in its
                                        Transactions tab — one implementation, so the
                                        two surfaces cannot drift. */}
                                    <button
                                        onClick={() => { setShowTransactions(true); setShowMoreMenu(false); }}
                                        style={{
                                            width: '100%', padding: '10px 16px', border: 'none', background: 'none',
                                            textAlign: 'left', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#212529',
                                            borderBottom: '1px solid #f1f5f9',
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                                    >
                                        {t('Transactions') || 'Transactions'}
                                    </button>
                                    <button
                                        onClick={() => { handleEndSession(); setShowMoreMenu(false); }}
                                        style={{
                                            width: '100%', padding: '10px 16px', border: 'none', background: 'none',
                                            textAlign: 'left', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#dc2626',
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.background = '#fef2f2'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                                    >
                                        {t('end_session') || 'End Session'}
                                    </button>
                                    {/* Printing toggles — relocated here from the sales canvas (both surfaces) */}
                                    {(printingConfigLoaded || translationsConfigLoaded) && (
                                        <div className="border-t border-gray-200" onClick={(e) => e.stopPropagation()}>
                                            {printingConfigLoaded && (
                                                <label className="flex items-center gap-2 px-4 py-2.5 cursor-pointer select-none">
                                                    <div className="relative">
                                                        <input
                                                            type="checkbox"
                                                            className="sr-only peer"
                                                            checked={printingEnabled}
                                                            onChange={(e) => handlePrintingToggle(e.target.checked)}
                                                        />
                                                        <div className="w-8 h-4 bg-gray-200 rounded-full peer peer-checked:bg-primary-600 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:after:translate-x-4" />
                                                    </div>
                                                    <span className="text-xs text-gray-600">{t('kitchen_bar_printing')}</span>
                                                    <span className={`text-xs font-medium ${printingEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                                                        {printingEnabled ? t('enabled') : t('disabled')}
                                                    </span>
                                                </label>
                                            )}
                                            {translationsConfigLoaded && (
                                                <label className="flex items-center gap-2 px-4 py-2.5 cursor-pointer select-none border-t border-gray-100">
                                                    <div className="relative">
                                                        <input
                                                            type="checkbox"
                                                            className="sr-only peer"
                                                            checked={receiptTranslationsEnabled}
                                                            onChange={(e) => handleReceiptTranslationsToggle(e.target.checked)}
                                                        />
                                                        <div className="w-8 h-4 bg-gray-200 rounded-full peer peer-checked:bg-green-500 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:after:translate-x-4" />
                                                    </div>
                                                    <span className="text-xs text-gray-600">{t('receipt_translations')}</span>
                                                    <span className={`text-xs font-medium ${receiptTranslationsEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                                                        {receiptTranslationsEnabled ? t('enabled') : t('disabled')}
                                                    </span>
                                                </label>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                    <button
                        onClick={() => {
                            const sid = activeSession?.session_id || '';
                            sessionStorage.setItem('lockScreen_sessionId', sid);
                            sessionStorage.setItem('lockScreen_station', 'Cashier Sales');
                            sessionStorage.setItem('lockScreen_returnPath', '/functions/cashier-sales');
                            navigate('/functions/lock');
                        }}
                        style={{
                            padding: '4px 12px', borderRadius: 6,
                            fontSize: 11, fontWeight: 700,
                            background: '#ffffff', color: '#334155',
                            border: 'none', cursor: 'pointer',
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            lineHeight: 1,
                            transition: 'background 150ms ease',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = '#ffffff'; }}
                    >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                        {!isMobile && t('Lock')}
                    </button>
                    <button
                        onClick={() => navigate('/functions')}
                        style={{
                            padding: '4px 12px', borderRadius: 6,
                            fontSize: 11, fontWeight: 700,
                            background: 'transparent', color: '#fecaca',
                            border: '1.5px solid #fca5a5', cursor: 'pointer',
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            lineHeight: 1, marginLeft: 4,
                            transition: 'background 150ms ease, color 150ms ease',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(220,38,38,0.15)';
                            e.currentTarget.style.color = '#fee2e2';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.color = '#fecaca';
                        }}
                    >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                            <polyline points="16 17 21 12 16 7" />
                            <line x1="21" y1="12" x2="9" y2="12" />
                        </svg>
                        {!isMobile && (t('sign_out') || 'Sign Out')}
                    </button>
                </div>
            </div>

            {/* ── ORDERS PANEL (slides from left) */}
            <OrdersPanel
                isOpen={ordersPanelOpen}
                onClose={() => setOrdersPanelOpen(false)}
                pendingOrders={pendingOrders}
                printedOrders={printedOrders}
                diningOptions={diningOptions}
                diningOptionObjects={diningOptionObjects}
                currency={currency}
                onOrderClick={(key, order) => {
                    setOrdersPanelOpen(false);
                    setSelectedTableOrderKey(key);
                    setSelectedTableOrder(order);
                    setTableOrderModalOpen(true);
                }}
                t={t}
                language={language}
                getEntityTranslation={getEntityTranslation}
                translateDynamic={translateDynamic}
                translationDetails={translationDetails}
            />

            {/* ── ALERTS */}
            {error && (
                <div className="flex-shrink-0 px-4 pt-2">
                    <Alert type="error" message={error} onClose={() => setError('')} />
                </div>
            )}
            {success && (
                <div className="flex-shrink-0 px-4 pt-2">
                    <Alert type="success" message={success} onClose={() => setSuccess('')} />
                </div>
            )}
            {printerWarning && (
                <div className="flex-shrink-0 px-4 pt-2">
                    <Alert type="warning" message={printerWarning} onClose={() => setPrinterWarning('')} />
                </div>
            )}

            {/* ── BILL REQUEST NOTIFICATIONS */}
            {billNotifications.length > 0 && (
                <div className="flex-shrink-0 px-4 pt-2 flex flex-col gap-1.5">
                    {billNotifications.map(notif => (
                        <div key={notif.id}
                            className="flex items-center justify-between rounded-lg px-4 py-2.5 animate-pulse"
                            style={{ background: '#fffbeb', border: '1.5px solid #f59e0b' }}>
                            <div className="flex items-center gap-3">
                                <svg className="w-5 h-5 text-amber-500 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/>
                                </svg>
                                <span className="text-sm font-bold text-amber-800">
                                    {t('table') || 'Table'} {notif.tableNumber}
                                    {notif.guestName ? ` — ${notif.guestName}` : ''}
                                    {' '}{t('requested_bill') || 'requested the bill'}
                                </span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <button onClick={() => {
                                    const doName = Object.keys(diningOptionObjects).find(
                                        name => String(diningOptionObjects[name]?.id) === String(notif.diningOptionId)
                                    );
                                    if (doName) {
                                        const tableKey = `${doName}-${notif.tableNumber}`;
                                        if (pendingOrders[tableKey]) {
                                            setSelectedTableOrderKey(tableKey);
                                            setSelectedTableOrder(pendingOrders[tableKey]);
                                            setTableOrderModalOpen(true);
                                        } else {
                                            setSelectedDiningOption(doName);
                                            setTableNumber(notif.tableNumber.toString());
                                        }
                                    }
                                    setBillNotifications(prev => prev.filter(n => n.id !== notif.id));
                                }}
                                    className="px-3 py-1 rounded-md text-xs font-bold transition-all"
                                    style={{ background: '#f59e0b', color: '#fff' }}>
                                    {t('view_order') || 'View Order'}
                                </button>
                                <button onClick={() => setBillNotifications(prev => prev.filter(n => n.id !== notif.id))}
                                    className="text-amber-500 hover:text-amber-700 p-1">
                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* ── TWO-PANEL BODY */}
            {isBusinessDay ? (
                <div className="flex flex-1 overflow-hidden">

                    {/* LEFT PANEL */}
                    <div className={isMobile ? 'w-full flex flex-col pb-16' : 'flex flex-col w-[65%] border-r border-gray-200 bg-gray-50 overflow-hidden'}>
                        {/* Dining option pill row + Floor Plan pill (persistent) */}
                        <div className={`flex-shrink-0 flex gap-2 overflow-x-auto px-3 pt-3 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] rounded-lg motion-safe:transition-shadow ${pillHighlight ? 'ring-2 ring-primary-400' : ''}`}>
                            {diningOptions.map(option => (
                                <button
                                    key={option}
                                    onClick={() => handleDiningPillSelect(option)}
                                    className={`shrink-0 whitespace-nowrap rounded-full border px-4 text-sm font-semibold transition-colors ${isMobile ? 'min-h-[40px]' : 'min-h-[34px]'} ${selectedDiningOption === option
                                        ? 'border-primary-600 bg-primary-600 text-white'
                                        : 'border-gray-300 bg-white text-gray-700'}`}
                                >
                                    {getEntityTranslation(diningOptionObjects[option], language, translationDetails) || translateDynamic(option, t)}
                                </button>
                            ))}
                            {(() => {
                                const layoutDOs = diningOptions.filter(name => {
                                    const obj = diningOptionObjects[name];
                                    if (!obj?.tables_config || typeof obj.tables_config !== 'object' || Array.isArray(obj.tables_config)) return false;
                                    return Object.values(obj.tables_config).some(v => {
                                        const entry = Array.isArray(v) && v.length > 0 ? v[0] : v;
                                        return entry && entry.x !== undefined && entry.y !== undefined;
                                    });
                                });
                                if (layoutDOs.length === 0) return null;
                                return (
                                    <button
                                        onClick={() => { setFloorPlanDO(layoutDOs[0]); setShowFloorPlanModal(true); }}
                                        className={`shrink-0 whitespace-nowrap rounded-full border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-700 flex items-center gap-1.5 ${isMobile ? 'min-h-[40px]' : 'min-h-[34px]'}`}
                                    >
                                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
                                        </svg>
                                        {t('floor_plan') || 'Floor Plan'}
                                    </button>
                                );
                            })()}
                        </div>

                        {/* Selection body: table picker (table-service) OR browse grid */}
                        <div className="flex flex-col flex-1 overflow-hidden px-3 pt-1 pb-2">

                            {needsTableSelection ? (
                                <div className="flex flex-col flex-1 overflow-hidden">
                                    <div className="flex items-center justify-between mb-3">
                                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                            {t('select_table') || 'Select Table'}
                                        </p>
                                        <div className="flex gap-2 text-xs">
                                            <button
                                                onClick={() => setTableViewMode('numbered')}
                                                className={`px-2 py-1 rounded ${tableViewMode === 'numbered' ? 'bg-primary-100 text-primary-700 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
                                            >
                                                {t('list') || 'List'}
                                            </button>
                                            {Array.isArray(availableTableNumbers[selectedDiningOption]) && (
                                                <button
                                                    onClick={() => setTableViewMode('visual')}
                                                    className={`px-2 py-1 rounded ${tableViewMode === 'visual' ? 'bg-primary-100 text-primary-700 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
                                                >
                                                    {t('visual') || 'Visual'}
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Random number input + pending orders list */}
                                    {availableTableNumbers[selectedDiningOption] === 'random_number' && (() => {
                                        const prefix = `${selectedDiningOption}-`;
                                        const activePending = Object.entries(pendingOrders)
                                            .filter(([key]) => key.startsWith(prefix))
                                            .map(([key, order]) => ({ key, number: key.slice(prefix.length), order }));
                                        return (
                                            <div className="flex flex-col flex-1 overflow-hidden">
                                                <div className="flex gap-2 mb-3">
                                                    <input
                                                        type="text"
                                                        placeholder={`${t('enter') || 'Enter'} ${getEntityTranslation(diningOptionObjects[selectedDiningOption], language, translationDetails) || translateDynamic(selectedDiningOption, t)} ${t('number') || 'number'}`}
                                                        value={tableNumber}
                                                        onChange={(e) => setTableNumber(e.target.value)}
                                                        onKeyDown={(e) => { if (e.key === 'Enter' && tableNumber.trim()) { setTableNumberConfirmed(true); } }}
                                                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
                                                        autoFocus
                                                    />
                                                    <button
                                                        disabled={!tableNumber.trim()}
                                                        className="bg-primary-600 text-white rounded-md px-4 py-2 text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                                                        onClick={() => { setTableNumberConfirmed(true); }}
                                                    >
                                                        {t('confirm') || 'Confirm'}
                                                    </button>
                                                </div>
                                                {activePending.length > 0 && (
                                                    <div className="flex-1 overflow-y-auto">
                                                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
                                                            {t('active_orders') || 'Active Orders'} ({activePending.length})
                                                        </p>
                                                        <div className={isMobile ? "grid grid-cols-3 gap-2" : "grid grid-cols-3 sm:grid-cols-4 gap-2"}>
                                                            {activePending.map(({ key, number, order }) => (
                                                                <SelectableCard
                                                                    key={key}
                                                                    label={`#${number}`}
                                                                    selected={false}
                                                                    customStyle={{ backgroundColor: '#fef9c3', borderColor: '#eab308' }}
                                                                    onClick={() => {
                                                                        setSelectedTableOrderKey(key);
                                                                        setSelectedTableOrder(order);
                                                                        setTableOrderModalOpen(true);
                                                                    }}
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}

                                    {/* Numbered grid */}
                                    {Array.isArray(availableTableNumbers[selectedDiningOption]) && tableViewMode === 'numbered' && (
                                        <div className="flex-1 overflow-y-auto">
                                            {/* Legend */}
                                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                                                {[
                                                    { color: '#10B981', label: t('available') || 'Available' },
                                                    { color: '#3B82F6', label: t('seated') || 'Seated' },
                                                    { color: '#EF4444', label: t('ordered') || 'Ordered' },
                                                    { color: '#d97706', label: t('awaiting_bill') || 'Awaiting Bill' },
                                                    { color: '#94a3b8', label: t('paid') || 'Paid' },
                                                ].map(s => (
                                                    <div key={s.label} className="flex items-center gap-1">
                                                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.color }} />
                                                        <span className="text-[10px] font-semibold text-gray-400">{s.label}</span>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className={isMobile ? 'grid grid-cols-3 gap-2' : 'grid grid-cols-4 sm:grid-cols-5 gap-2'}>
                                                {availableTableNumbers[selectedDiningOption].map(num => {
                                                    const tableKey = `${selectedDiningOption}-${num}`;
                                                    const hasPendingOrder = !!pendingOrders[tableKey];
                                                    const numStr = num.toString();
                                                    // Resolve merged members to their anchor so list view matches the visual floor plan.
                                                    const occ = resolveOccupation(
                                                        activeOccupations.filter(o => String(o.dining_option_id) === String(diningOptionObjects[selectedDiningOption]?.id)),
                                                        numStr
                                                    );
                                                    let statusColor = null;
                                                    let textColor = '#374151';
                                                    if (occ) {
                                                        if (occ.paid_at) { statusColor = '#94a3b8'; textColor = '#fff'; }
                                                        else if (occ.bill_requested_at) { statusColor = '#d97706'; textColor = '#fff'; }
                                                        else if (hasPendingOrder || occ.first_order_at) { statusColor = '#EF4444'; textColor = '#fff'; }
                                                        else { statusColor = '#3B82F6'; textColor = '#fff'; }
                                                    } else if (hasPendingOrder) {
                                                        statusColor = '#EF4444'; textColor = '#fff';
                                                    } else {
                                                        statusColor = '#10B981'; textColor = '#fff';
                                                    }
                                                    const isSelected = tableNumber === numStr;
                                                    // Part of a merged group? (resolved to a different anchor, or is an anchor with members)
                                                    const isGrouped = !!occ && (String(occ.table_number) !== numStr || (occ.merged_table_numbers || []).length > 0);
                                                    const groupLabel = isGrouped
                                                        ? (String(occ.table_number) === numStr ? ` · +${(occ.merged_table_numbers || []).length}` : ` · ↳${occ.table_number}`)
                                                        : '';
                                                    return (
                                                        <SelectableCard
                                                            key={num}
                                                            label={`${typeof num === 'string' ? num : `T${num}`}${groupLabel}`}
                                                            selected={isSelected}
                                                            icon={isGrouped ? (
                                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                                                                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                                                                </svg>
                                                            ) : null}
                                                            onClick={() => {
                                                                // Resolve merged members to the anchor so the shared order opens
                                                                // (the merged bill lives under the anchor's key), matching visual view.
                                                                const targetNum = occ ? String(occ.table_number) : numStr;
                                                                const targetKey = `${selectedDiningOption}-${targetNum}`;
                                                                if (pendingOrders[targetKey]) {
                                                                    setSelectedTableOrderKey(targetKey);
                                                                    setSelectedTableOrder(pendingOrders[targetKey]);
                                                                    setTableOrderModalOpen(true);
                                                                    return;
                                                                }
                                                                if (checkEditingConflict('changeTableNumber', targetNum)) return;
                                                                setTableNumber(targetNum);
                                                            }}
                                                            customStyle={{
                                                                backgroundColor: statusColor,
                                                                borderColor: isSelected ? '#facc15' : (isGrouped ? '#2563eb' : statusColor),
                                                                color: textColor,
                                                                borderWidth: (isSelected || isGrouped) ? '2px' : '1px',
                                                                borderStyle: isGrouped && !isSelected ? 'dashed' : 'solid',
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Visual grid */}
                                    {Array.isArray(availableTableNumbers[selectedDiningOption]) && tableViewMode === 'visual' && (
                                        <div className="flex-1 overflow-hidden">
                                            <VisualTableSelector
                                                selectedDiningOption={selectedDiningOption}
                                                selectedTable={tableNumber}
                                                onTableSelect={(tableNum) => {
                                                    if (checkEditingConflict('changeTableNumber', tableNum)) return;
                                                    setTableNumber(tableNum.toString());
                                                }}
                                                onTableWithOrderClick={(tableNum, order) => {
                                                    const tableKey = `${selectedDiningOption}-${tableNum}`;
                                                    setSelectedTableOrderKey(tableKey);
                                                    setSelectedTableOrder(order);
                                                    setTableOrderModalOpen(true);
                                                }}
                                                pendingOrders={pendingOrders}
                                                printedOrders={printedOrders}
                                                diningOptions={fullDiningOptions}
                                                onDataChange={refreshDiningOptions}
                                                isVisible={tableViewMode === 'visual'}
                                                currency={currency}
                                                occupations={activeOccupations.filter(o => String(o.dining_option_id) === String(diningOptionObjects[selectedDiningOption]?.id))}
                                                scaleToFit={isMobile}
                                            />
                                        </div>
                                    )}
                                </div>
                            ) : (
                                (() => {
                                const PLACEHOLDER = <svg className="w-10 h-10 opacity-30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><line x1="12" y1="2" x2="12" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/><line x1="19.07" y1="4.93" x2="4.93" y2="19.07"/></svg>;
                                return (
                                    <div className="flex flex-col flex-1 overflow-hidden">
                                        {/* Search */}
                                        <div className="relative mb-2 flex-shrink-0">
                                            <input
                                                type="text"
                                                placeholder={t('search_products_placeholder') || 'Search products...'}
                                                value={productSearch}
                                                onChange={(e) => { setProductSearch(e.target.value); }}
                                                className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                                            />
                                            <svg className="absolute left-2 top-2 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                            {productSearch && (
                                                <button onClick={() => { setProductSearch(''); }} className="absolute right-2 top-2">
                                                    <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                        {/* Reorder control + category chips. The category-level
                                            "All" chip is gone (client request 2026-08-26): it
                                            rendered every product in one unusable wall. The
                                            SUBCATEGORY "All" below stays — that means "everything
                                            in this category", which is a different, useful thing. */}
                                        {categories.length > 0 && (
                                          <>
                                            <div className="flex justify-end pb-1 flex-shrink-0">
                                                {reordering ? (
                                                    <button
                                                        type="button"
                                                        data-testid="reorder-done"
                                                        onClick={async () => {
                                                            setReordering(false);
                                                            // Reload BEFORE dropping the local
                                                            // arrangement: `products` state still
                                                            // holds the pre-reorder order, so
                                                            // clearing first made the menu snap
                                                            // back until a page refresh.
                                                            try { await loadCatalog(); }
                                                            catch (err) { console.error('Failed to reload catalogue after reorder:', err); }
                                                            setLocalOrder(null);
                                                            // Cleared after the reload for the same
                                                            // reason: `categories` still holds the
                                                            // old order until loadCatalog resolves.
                                                            setLocalCategoryOrder(null);
                                                        }}
                                                        className="rounded-full border border-primary-600 bg-primary-600 px-3 py-1 text-xs font-bold text-white"
                                                    >
                                                        {t('done') || 'Done'}
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        data-testid="reorder-toggle"
                                                        onClick={() => setReorderGateOpen(true)}
                                                        className="rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-bold text-gray-600"
                                                    >
                                                        {t('reorder_menu') || 'Reorder menu'}
                                                    </button>
                                                )}
                                            </div>
                                            <div className="flex gap-2 overflow-x-auto pb-1 mb-1 flex-shrink-0 [-ms-overflow-style:none] [scrollbar-width:none]">
                                                {displayCategories.map((cat, catIndex) => (
                                                    reordering ? (
                                                        // In reorder mode the chip is the thing being
                                                        // moved, not a filter — tapping it must not
                                                        // switch category under the manager mid-drag.
                                                        <div
                                                            key={cat}
                                                            className="shrink-0 whitespace-nowrap rounded-full border border-gray-300 bg-white px-2 py-1.5 text-sm font-semibold text-gray-600 flex items-center gap-1"
                                                        >
                                                            <button
                                                                type="button"
                                                                data-testid={`category-move-back-${cat}`}
                                                                aria-label={`${cat}: ${t('move_left') || 'Move left'}`}
                                                                disabled={catIndex === 0}
                                                                onClick={() => moveCategory(cat, -1)}
                                                                className="w-7 h-7 rounded-md border border-gray-300 bg-white text-gray-700 text-[10px] flex items-center justify-center disabled:opacity-35"
                                                            >&#9664;</button>
                                                            <span className="flex items-center gap-1.5 px-1">
                                                                {renderEntityImage(categoryObjects[cat]?.image_url, 20) || getCategoryIcon(categoryIcons[cat] || 'star', { size: 18, color: '#94a3b8' })}
                                                                {getEntityTranslation(categoryObjects[cat], language, translationDetails) || translateDynamic(cat, t)}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                data-testid={`category-move-fwd-${cat}`}
                                                                aria-label={`${cat}: ${t('move_right') || 'Move right'}`}
                                                                disabled={catIndex === displayCategories.length - 1}
                                                                onClick={() => moveCategory(cat, 1)}
                                                                className="w-7 h-7 rounded-md border border-gray-300 bg-white text-gray-700 text-[10px] flex items-center justify-center disabled:opacity-35"
                                                            >&#9654;</button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            key={cat}
                                                            onClick={() => {
                                                                if (cat !== selectedCategory) {
                                                                    setSelectedCategory(cat);
                                                                    setSelectedSubcategory(null);
                                                                    setProductSearch('');
                                                                }
                                                            }}
                                                            className={`shrink-0 whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors flex items-center gap-1.5 ${
                                                                selectedCategory === cat
                                                                    ? 'bg-primary-600 text-white border-primary-600'
                                                                    : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400'
                                                            }`}
                                                        >
                                                            {renderEntityImage(categoryObjects[cat]?.image_url, 20) || getCategoryIcon(categoryIcons[cat] || 'star', { size: 18, color: selectedCategory === cat ? '#fff' : '#94a3b8' })}
                                                            {getEntityTranslation(categoryObjects[cat], language, translationDetails) || translateDynamic(cat, t)}
                                                        </button>
                                                    )
                                                ))}
                                            </div>
                                          </>
                                        )}
                                        {/* Subcategory chips (contextual — outlined, lighter) */}
                                        {selectedCategory && subcategories.length > 0 && (
                                            <div className="flex gap-2 overflow-x-auto pb-1 mb-1 flex-shrink-0 [-ms-overflow-style:none] [scrollbar-width:none]">
                                                <button
                                                    onClick={() => { setSelectedSubcategory(null); }}
                                                    className={`shrink-0 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                                                        !selectedSubcategory
                                                            ? 'border-primary-500 text-primary-700 bg-primary-50'
                                                            : 'border-gray-300 text-gray-600 bg-white hover:border-primary-400'
                                                    }`}
                                                >
                                                    {t('all') || 'All'}
                                                </button>
                                                {subcategories.map(subcat => (
                                                    <button
                                                        key={subcat.id}
                                                        onClick={() => {
                                                            setSelectedSubcategory(selectedSubcategory === subcat.name ? null : subcat.name);
                                                        }}
                                                        className={`shrink-0 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition-colors flex items-center gap-1.5 ${
                                                            selectedSubcategory === subcat.name
                                                                ? 'border-primary-500 text-primary-700 bg-primary-50'
                                                                : 'border-gray-300 text-gray-600 bg-white hover:border-primary-400'
                                                        }`}
                                                    >
                                                        {subcat.image_url && renderEntityImage(subcat.image_url, 16)}
                                                        {getEntityTranslation(subcat, language, translationDetails) || getTransliteration(subcat.name, language, t)}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                        {/* Product grid */}
                                        <div className="flex-1 overflow-y-auto">
                                            <div className={isMobile ? 'grid grid-cols-3 gap-2' : 'grid grid-cols-4 gap-2'}>
                                                {displayProducts.map((product, productIndex) => {
                                                    let displayStock, isOutOfStock;
                                                    if (product.stock_calculation_type === 'auto') {
                                                        const stockData = bulkCalculatedStock[product.product_code];
                                                        const calc = stockData ? stockData.available_stock : 0;
                                                        displayStock = stockData ? (calc === Infinity ? '∞' : calc) : '...';
                                                        isOutOfStock = calc < 1;
                                                    } else {
                                                        displayStock = product.quantity === -9999 ? '∞' : product.quantity;
                                                        isOutOfStock = product.quantity < 1 && product.quantity !== -9999;
                                                    }
                                                    return (
                                                        <div key={product.product_code} className="relative">
                                                            <SelectableCard
                                                                label={`${getProductName(product, language, translationDetails) ?? getTransliteration(product.name, language, t)} (${displayStock})`}
                                                                selected={selectedProducts.some(p => p.product_code === product.product_code)}
                                                                onClick={reordering ? () => {} : () => handleAddProduct(product)}
                                                                disabled={isOutOfStock && !reordering}
                                                                icon={renderEntityImage(product.image_url, 40) || PLACEHOLDER}
                                                                layout="icon-top"
                                                            />
                                                            {reordering && (
                                                                <div className="absolute top-1 right-1 flex flex-col gap-1">
                                                                    <button
                                                                        type="button"
                                                                        data-testid={`move-up-${product.product_code}`}
                                                                        aria-label={t('move_up') || 'Move up'}
                                                                        disabled={productIndex === 0}
                                                                        onClick={(e) => { e.stopPropagation(); moveProduct(product.product_code, -1); }}
                                                                        className="h-7 w-7 rounded-md border border-gray-300 bg-white text-sm font-bold disabled:opacity-35"
                                                                    >&#8593;</button>
                                                                    <button
                                                                        type="button"
                                                                        data-testid={`move-down-${product.product_code}`}
                                                                        aria-label={t('move_down') || 'Move down'}
                                                                        disabled={productIndex === displayProducts.length - 1}
                                                                        onClick={(e) => { e.stopPropagation(); moveProduct(product.product_code, 1); }}
                                                                        className="h-7 w-7 rounded-md border border-gray-300 bg-white text-sm font-bold disabled:opacity-35"
                                                                    >&#8595;</button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            {displayProducts.length === 0 && (
                                                <p className="text-gray-500 text-center py-4 text-sm">{t('no_products_in_category') || 'No products'}</p>
                                            )}
                                        </div>
                                    </div>
                                );
                                })()
                            )}

                        </div>

                        {/* Apply offer button — shows when any eligible promo exists */}
                        {promoAllEligible.length > 0 && (
                            <div className="flex-shrink-0 border-t px-3 py-2" style={{ borderColor: '#e5e7eb' }}>
                                <button
                                    onClick={() => setShowPromoModal(true)}
                                    className="flex w-full flex-col items-center gap-1 py-2 rounded-lg text-xs font-semibold transition-opacity"
                                    style={{
                                        backgroundColor: appliedPromo ? '#059669' : '#059669cc',
                                        color: '#fff',
                                    }}
                                >
                                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"/></svg>
                                    <span>{t('apply_offer')}</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* RIGHT PANEL — order summary */}
                    {!isMobile && (
                    <div className="flex flex-col w-[35%] bg-white overflow-hidden">
                        {renderCartPanel()}
                    </div>
                    )}

                </div>
            ) : (
                <div className="flex flex-1 items-center justify-center">
                    <div className="text-center max-w-md">
                        <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">{t('sales_interface_locked')}</h3>
                        <p className="text-gray-600 mb-4">{t('start_business_day_to_access_sales_features')}</p>
                    </div>
                </div>
            )}

            {isMobile && isBusinessDay && (() => {
                const { total: mobileBarTotal } = handleCalculateTotals();
                const mobileCartTotal = Math.max(0, mobileBarTotal - promoDiscountAmount);
                return (
                    <>
                        {/* bottom cart bar — always visible during a sale */}
                        <button
                            onClick={() => setMobileCartOpen(true)}
                            aria-label={t('view_cart') || 'View cart'}
                            className="fixed bottom-3 left-3 right-3 z-40 flex items-center justify-between rounded-xl bg-gray-900 px-4 text-white font-semibold shadow-lg min-h-[48px]"
                        >
                            <span className="flex items-center gap-2">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
                                {selectedProducts.length} {t('items') || 'items'}
                            </span>
                            <span className="tabular-nums">{currency}{mobileCartTotal.toFixed(2)}</span>
                        </button>

                        {/* scrim */}
                        <div
                            onClick={() => setMobileCartOpen(false)}
                            className={`fixed inset-0 z-40 bg-black/50 transition-opacity duration-200 motion-reduce:transition-none ${mobileCartOpen ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
                        />

                        {/* slide-up cart sheet */}
                        <div
                            role="dialog"
                            aria-modal="true"
                            aria-label={t('cart') || 'Cart'}
                            className={`fixed inset-x-0 bottom-0 z-50 flex max-h-[85dvh] flex-col rounded-t-2xl bg-white shadow-2xl transition-transform duration-300 ease-out motion-reduce:transition-none ${mobileCartOpen ? 'translate-y-0' : 'translate-y-full'}`}
                        >
                            <button
                                onClick={() => setMobileCartOpen(false)}
                                aria-label={t('close') || 'Close'}
                                className="flex min-h-[44px] w-full items-center justify-center"
                            >
                                <span className="h-1.5 w-10 rounded-full bg-gray-300" />
                            </button>
                            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                                {renderCartPanel()}
                            </div>
                        </div>
                    </>
                );
            })()}

            {/* ── Promo picker modal ───────────────────────────────── */}
            {showPromoModal && (
                <div className="fixed inset-0 z-50 flex items-end justify-center">
                    <div
                        onClick={() => setShowPromoModal(false)}
                        className="absolute inset-0 bg-black bg-opacity-30"
                    />
                    <div className="relative bg-white rounded-t-2xl w-full max-w-md p-4 shadow-xl">
                        <h3 className="text-base font-bold mb-3 text-gray-900">{t('select_offer')}</h3>
                        {promoAllEligible.length === 0 ? (
                            <p className="text-sm text-gray-500 py-4 text-center">{t('no_offers_available')}</p>
                        ) : (
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                {promoAllEligible.map(({ promotion, discount }) => (
                                    <button
                                        key={promotion.promotion_id}
                                        onClick={() => selectPromo({ promotion, discount })}
                                        className={`w-full text-left p-3 rounded-lg border transition-colors ${
                                            appliedPromo?.promotion?.promotion_id === promotion.promotion_id
                                                ? 'border-green-500 bg-green-50'
                                                : 'border-gray-200 hover:border-green-300 hover:bg-green-50'
                                        }`}
                                    >
                                        <div className="flex justify-between items-center">
                                            <span className="font-medium text-sm text-gray-900">{promotion.name}</span>
                                            <span className="text-green-600 text-sm font-bold">−{currency}{discount.toFixed(2)}</span>
                                        </div>
                                        {promotion.description && (
                                            <p className="text-xs text-gray-500 mt-1">{promotion.description}</p>
                                        )}
                                        <p className="text-xs text-green-700 mt-0.5">{t('saving')}: {currency}{discount.toFixed(2)}</p>
                                    </button>
                                ))}
                            </div>
                        )}
                        <button
                            onClick={() => setShowPromoModal(false)}
                            className="mt-3 w-full py-2 rounded-lg border border-gray-300 text-sm text-gray-700"
                        >
                            {t('cancel')}
                        </button>
                    </div>
                </div>
            )}

            {/* ── BACKGROUND COMPONENTS */}
            <SplitBillManager
                ref={splitBillRef}
                pendingTransaction={pendingTransaction}
                setPendingTransaction={setPendingTransaction}
                currentConfirmOrder={currentConfirmOrder}
                setCurrentConfirmOrder={setCurrentConfirmOrder}
                pendingOrders={pendingOrders}
                setPendingOrders={setPendingOrders}
                printedOrders={printedOrders}
                setPrintedOrders={setPrintedOrders}
                pendingOrderIds={pendingOrderIds}
                cashierName={cashierName}
                selectedDiningOption={selectedDiningOption}
                selectedPaymentMethod={selectedPaymentMethod}
                tableNumber={tableNumber}
                notes={notes}
                selectedProducts={selectedProducts}
                setSelectedProducts={setSelectedProducts}
                currency={currency}
                paymentMethods={paymentMethods}
                tenantConfig={tenantConfig}
                printableCategories={printableCategories}
                brandName={brandName}
                tenantId={tenantId}
                handleCalculateTotals={handleCalculateTotals}
                loadTransactions={loadTransactions}
                setSuccess={setSuccess}
                setError={setError}
                setLoading={setLoading}
                loading={loading}
                setNotes={setNotes}
                setDiscountAmount={setDiscountAmount}
                setPayableAmount={setPayableAmount}

                setSelectedCategory={setSelectedCategory}
                setSelectedSubcategory={setSelectedSubcategory}
                setSelectedDiningOption={setSelectedDiningOption}
                setTableNumber={setTableNumber}
                setAddServiceCharge={setAddServiceCharge}
                setDiscountType={setDiscountType}
                setDiscountPercentage={setDiscountPercentage}
                setDiscountMode={setDiscountMode}
                onSplitCompleted={() => {
                    // Reset form state after split completion
                    setSelectedProducts([]);
                    setNotes('');
                    setDiscountType('amount');
                    setDiscountPercentage(0);
                    setDiscountAmount(0);
                    setPayableAmount(0);
        
                    setSelectedCategory(null);
                    setSelectedSubcategory(null);
                    setSelectedDiningOption(null);
                    setTableNumber('');
                    setTableNumberConfirmed(false);
                    setAddServiceCharge(false);
                    setAddTax(false);
                    setDiscountMode('direct');
                    setMobileCartOpen(false);

                    setSelectedPaymentMethod(null);  // Reset to no selection
                    setSelectedModalPaymentMethod(null);  // Reset modal selection
                }}
            />

            {/* ── MODALS */}

            {/* Payment Confirmation Modal */}
            <PaymentConfirmModal
                isOpen={paymentConfirmModalOpen}
                onClose={() => {
                    setPaymentConfirmModalOpen(false);
                    setSelectedModalPaymentMethod(null);
                }}
                currentConfirmOrder={currentConfirmOrder}
                selectedPaymentMethod={selectedPaymentMethod}
                selectedModalPaymentMethod={selectedModalPaymentMethod}
                setSelectedModalPaymentMethod={setSelectedModalPaymentMethod}
                paymentMethods={paymentMethods}
                pendingTransaction={pendingTransaction}
                processTransactionWithPayment={processTransactionWithPayment}
                selectedDiningOption={selectedDiningOption}
                diningOptionObjects={diningOptionObjects}
                tableNumber={tableNumber}
                currency={currency}
            />


            {/* Add-on Selection Modal */}
            <AddonSelectionModal
                isOpen={addonModalOpen}
                onClose={() => setAddonModalOpen(false)}
                currentProduct={currentProduct}
                groupIds={currentProductGroups}
                catalog={activeCatalog}
                tree={modifierTree}
                onToggle={toggleModifier}
                unmet={modifierUnmet}
                flagUnmet={modifierFlagUnmet}
                lineTotal={modifierLineTotal}
                onAdd={addProductWithAddons}
                currency={currency}
            />


            {/* Order Editing Protection Modal */}
            <EditingProtectionModal
                isOpen={editingProtectionModalOpen}
                onCancel={handleCancelAction}
                onDiscard={handleDiscardAndContinue}
                onSave={handleSaveAndContinue}
                editingOrderKey={editingOrderKey}
                pendingOrders={pendingOrders}
            />


            {/* Table Order Modal */}
            <TableOrderModal
                isOpen={tableOrderModalOpen}
                onClose={() => {
                    setTableOrderModalOpen(false);
                    setSelectedTableOrderKey(null);
                    setSelectedTableOrder(null);
                }}
                selectedTableOrder={selectedTableOrder}
                selectedTableOrderKey={selectedTableOrderKey}
                printedOrders={printedOrders}
                currency={currency}
                pendingOrders={pendingOrders}
                pendingOrderIds={pendingOrderIds}
                tenantStorage={tenantStorage}
                setPrintedOrders={setPrintedOrders}
                setSelectedProducts={setSelectedProducts}
                setSelectedDiningOption={setSelectedDiningOption}
                setTableNumber={setTableNumber}
                setAddServiceCharge={setAddServiceCharge}
                setAddTax={setAddTax}
                setDiscountAmount={setDiscountAmount}
                setNotes={setNotes}
                setSelectedPaymentMethod={setSelectedPaymentMethod}
                setEditingOrderKey={setEditingOrderKey}
                setCurrentOrderId={setCurrentOrderId}
                setOrderToChangeTable={setOrderToChangeTable}
                setNewTableNumber={setNewTableNumber}
                setNewDiningOption={setNewDiningOption}
                setChangeTableModalOpen={setChangeTableModalOpen}
                setCurrentConfirmOrder={setCurrentConfirmOrder}
                setConfirmModalOpen={setConfirmModalOpen}
                handleDeleteOrder={handleDeleteOrder}
                handleClearPaidOrder={handleClearPaidOrder}
                printableCategories={printableCategories}
                setSuccess={setSuccess}
                setError={setError}
            />


            {/* Confirm Payment for Pending Order Modal */}
            <Modal
                isOpen={confirmModalOpen}
                onClose={() => setConfirmModalOpen(false)}
                title={t('select_payment_method')}
                footer={(
                    <button
                        type="button"
                        className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        onClick={() => setConfirmModalOpen(false)}
                    >
                        {t('cancel')}
                    </button>
                )}
            >
                {currentConfirmOrder && pendingOrders[currentConfirmOrder] && (
                    <div>
                        <p>{t('please_select_payment_method_for')} {pendingOrders[currentConfirmOrder].diningOption} - {pendingOrders[currentConfirmOrder].tableNumber}:</p>

                        <div className="grid grid-cols-2 gap-3 mt-4 mb-6">
                            {paymentMethods.map(method => (
                                <button
                                    key={method}
                                    // In the confirmation modal, update the button click handler
                                    onClick={async () => {
                                        try {
                                            const order = pendingOrders[currentConfirmOrder];
                                            if (!order) {
                                                setError(t('order_not_found'));
                                                return;
                                            }

                                            // FORCE SERVICE CHARGE for Dine In/Dine Out
                                            const updatedOrder = recalculateOrderWithServiceCharge(order);

                                            updatedOrder.paymentMethod = method;
                                            setConfirmModalOpen(false);

                                            setSelectedProducts(updatedOrder.products);
                                            setSelectedPaymentMethod(method);
                                            setSelectedDiningOption(updatedOrder.diningOption);
                                            setTableNumber(updatedOrder.tableNumber);
                                            setAddServiceCharge(updatedOrder.addServiceCharge); // Set service charge state
                                            setAddTax(updatedOrder.addTax || false);
                                            setDiscountAmount(updatedOrder.discount);
                                            setNotes(updatedOrder.notes);

                                            // Create transaction with updated totals
                                            const currentSalesDate = await tenantStorage.getState('salesDate');
                                            if (!currentSalesDate) {
                                                throw new Error('Sales date not found');
                                            }

                                            const transaction = createTransactionFromOrder(updatedOrder, new Date(), currentSalesDate);
                                            transaction.sales_date = currentSalesDate;
                                            transaction.payment_method = method;
                                            transaction.products_json = JSON.stringify(updatedOrder.products);

                                            setPendingTransaction(transaction);
                                            setPaymentConfirmModalOpen(true);
                                        } catch (error) {
                                            console.error('Error preparing transaction:', error);
                                            setError(`Failed to prepare transaction: ${error.message}`);
                                        }
                                    }}
                                    className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                >
                                    {translateDynamic(method, t)}
                                </button>
                            ))}
                        </div>

                        <div className="mt-4 border-t pt-4">
                            <h4 className="font-medium mb-2">{t('order_summary')}:</h4>
                            <ul className="space-y-1">
                                {pendingOrders[currentConfirmOrder].products.map((product, idx) => (
                                    <li key={idx} className="text-sm">
                                        {product.quantity}x {product.name} - {currency}{(product.quantity * product.selling_price).toFixed(2)}
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-3 pt-3 border-t">
                                {(() => {
                                    const order = recalculateOrderWithServiceCharge(pendingOrders[currentConfirmOrder]);
                                    return (
                                        <>
                                            <div className="flex justify-between text-sm">
                                                <span>{t('subtotal')}</span>
                                                <span>{currency}{(order.subtotal || 0).toFixed(2)}</span>
                                            </div>
                                            {order.discount > 0 && (
                                                <div className="flex justify-between text-sm">
                                                    <span>{t('discount')}:</span>
                                                    <span>{currency}{(order.discount || 0).toFixed(2)}</span>
                                                </div>
                                            )}
                                            {order.addServiceCharge && (
                                                <div className="flex justify-between text-sm">
                                                    <span>{t('service_charge_with_percent', { percent: Math.round((tenantConfig?.service_charge_rate || 0.12) * 100) })}:</span>
                                                    <span>{currency}{(order.serviceCharge || 0).toFixed(2)}</span>
                                                </div>
                                            )}
                                            {order.addTax && (order.tax || 0) > 0 && (
                                                <div className="flex justify-between text-sm">
                                                    <span>{t('tax_with_percent', { percent: Math.round((tenantConfig?.tax_rate || 0) * 100) })}:</span>
                                                    <span>{currency}{(order.tax || 0).toFixed(2)}</span>
                                                </div>
                                            )}
                                            <div className="flex justify-between font-medium mt-1">
                                                <span>{t('total')}</span>
                                                <span>{currency}{(order.total || 0).toFixed(2)}</span>
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>


            {/* Change Table Modal */}
            <ChangeTableModal
                isOpen={changeTableModalOpen}
                onClose={() => setChangeTableModalOpen(false)}
                orderToChangeTable={orderToChangeTable}
                pendingOrders={pendingOrders}
                diningOptions={diningOptions}
                availableTableNumbers={availableTableNumbers}
                newDiningOption={newDiningOption}
                setNewDiningOption={setNewDiningOption}
                newTableNumber={newTableNumber}
                setNewTableNumber={setNewTableNumber}
                onConfirm={async () => {
                    if (!orderToChangeTable || !newTableNumber || !newDiningOption) return;

                    try {
                        const originalOrder = pendingOrders[orderToChangeTable];
                        const newTableKey = `${newDiningOption}-${newTableNumber}`;

                        if (Object.keys(pendingOrders).includes(newTableKey)) {
                            setError(`${newDiningOption} ${newTableNumber} already has a pending order`);
                            return;
                        }

                        const orderId = pendingOrderIds[orderToChangeTable];
                        await tenantStorage.changeOrderTable(orderId, newTableKey, newDiningOption, newTableNumber);

                        const updatedOrder = { ...originalOrder, tableNumber: newTableNumber, diningOption: newDiningOption };

                        setPendingOrders(prev => {
                            const newOrders = { ...prev };
                            delete newOrders[orderToChangeTable];
                            newOrders[newTableKey] = updatedOrder;
                            return newOrders;
                        });

                        setPendingOrderIds(prev => {
                            const newOrderIds = { ...prev };
                            delete newOrderIds[orderToChangeTable];
                            newOrderIds[newTableKey] = orderId;
                            return newOrderIds;
                        });

                        setChangeTableModalOpen(false);
                        setSuccess(t('order_moved_to', { diningOption: newDiningOption, tableNumber: newTableNumber }));
                        setTimeout(() => setSuccess(''), 1000);
                    } catch (err) {
                        setError(t('failed_to_change_table'));
                    }
                }}
                confirmDisabled={!newTableNumber || !newDiningOption}
            />

            <DeletionConfirmModal
                isOpen={deletionConfirm.isModalOpen}
                onClose={deletionConfirm.handleCancel}
                onConfirm={deletionConfirm.handleConfirm}
                loading={deletionConfirm.loading}
                {...deletionConfirm.modalProps}
            />

            {/* Floor Plan Modal */}
            {showFloorPlanModal && (() => {
                const layoutDOs = diningOptions.filter(name => {
                    const obj = diningOptionObjects[name];
                    if (!obj?.tables_config || typeof obj.tables_config !== 'object' || Array.isArray(obj.tables_config)) return false;
                    return Object.values(obj.tables_config).some(v => {
                        const entry = Array.isArray(v) && v.length > 0 ? v[0] : v;
                        return entry && entry.x !== undefined && entry.y !== undefined;
                    });
                });
                const activeDO = floorPlanDO || layoutDOs[0];
                const layoutMap = {};
                layoutDOs.forEach(name => {
                    const obj = diningOptionObjects[name];
                    if (obj?.tables_config) layoutMap[name] = obj.tables_config;
                });
                return (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
                        onClick={() => setShowFloorPlanModal(false)}>
                        <div className="bg-white rounded-2xl shadow-2xl w-[90vw] max-w-[700px] max-h-[80vh] flex flex-col overflow-hidden"
                            onClick={e => e.stopPropagation()}>
                            <div className="flex items-center justify-between px-5 py-3 border-b">
                                <div className="flex items-center gap-3">
                                    <span className="text-sm font-bold text-gray-800">{t('floor_plan') || 'Floor Plan'}</span>
                                    {layoutDOs.length > 1 && (
                                        <div className="flex gap-1">
                                            {layoutDOs.map(name => (
                                                <button key={name}
                                                    onClick={() => setFloorPlanDO(name)}
                                                    className="px-3 py-1 rounded-lg text-xs font-semibold transition-all"
                                                    style={{
                                                        background: activeDO === name ? '#16a34a' : '#f1f5f9',
                                                        color: activeDO === name ? '#fff' : '#64748b',
                                                    }}>
                                                    {getEntityTranslation(diningOptionObjects[name], language, translationDetails) || name}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <button onClick={() => setShowFloorPlanModal(false)}
                                    className="text-gray-400 hover:text-gray-600 p-1">
                                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                </button>
                            </div>
                            <div className="flex-1 overflow-auto p-4" style={{ minHeight: 400 }}>
                                <VisualTableSelector
                                    selectedDiningOption={activeDO}
                                    selectedTable={null}
                                    onTableSelect={() => {}}
                                    pendingOrders={pendingOrders}
                                    printedOrders={printedOrders}
                                    diningOptions={layoutMap}
                                    isVisible={true}
                                    currency={currency}
                                    occupations={activeOccupations.filter(o => String(o.dining_option_id) === String(diningOptionObjects[activeDO]?.id))}
                                    scaleToFit={isMobile}
                                />
                            </div>
                            <div className="flex items-center gap-4 px-5 py-2.5 border-t text-[10px] text-gray-500">
                                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{background:'#10B981'}}/> {t('available') || 'Available'}</span>
                                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{background:'#3B82F6'}}/> {t('seated') || 'Seated'}</span>
                                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{background:'#EF4444'}}/> {t('ordered') || 'Ordered'}</span>
                                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{background:'#d97706'}}/> {t('awaiting_bill') || 'Awaiting Bill'}</span>
                                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{background:'#94a3b8'}}/> {t('paid') || 'Paid'}</span>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Manager approval gates (client request 2026-08-26). Rendered
                last so they layer above the cart panels and table overlays. */}
            <ManagerGate
                open={pendingDiscount != null}
                action="discount"
                context={pendingDiscount
                    ? { amount: pendingDiscount.amount, mode: discountMode }
                    : null}
                onApprove={onDiscountApproved}
                onCancel={() => setPendingDiscount(null)}
            />

            <ManagerGate
                open={reorderGateOpen}
                action="reorder_menu"
                context={{ category: selectedCategory }}
                onApprove={() => { setReorderGateOpen(false); setReordering(true); }}
                onCancel={() => setReorderGateOpen(false)}
            />

            <ManagerGate
                open={pendingDeleteKey != null}
                action="delete_order"
                context={pendingDeleteKey ? { orderKey: pendingDeleteKey } : null}
                onApprove={(approval) => {
                    const key = pendingDeleteKey;
                    setPendingDeleteKey(null);
                    openDeleteReasonModal(key, approval?.approved_by_name || null);
                }}
                onCancel={() => setPendingDeleteKey(null)}
            />

            {/* Transactions + refunds. A sheet here rather than a route: the
                cashier is mid-service and must come back to the same cart. */}
            {/* Portaled for the same reason ManagerGate is: this page mounts
                inside CashierStation's glass panel, whose backdrop-filter
                would otherwise become the containing block and clip the
                sheet. See pos-app/fixedOverlayPortals.test.js. */}
            {showTransactions && createPortal(
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label={t('Transactions') || 'Transactions'}
                    style={{
                        position: 'fixed', inset: 0, zIndex: 60,
                        background: 'rgba(0,0,0,0.45)', display: 'flex',
                        alignItems: 'flex-end', justifyContent: 'center',
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                    }}
                    onClick={() => setShowTransactions(false)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: '#f8fafc', width: 'min(720px, 100%)',
                            maxHeight: '88vh', overflowY: 'auto',
                            borderTopLeftRadius: 16, borderTopRightRadius: 16,
                            padding: 16,
                        }}
                    >
                        <div style={{
                            display: 'flex', alignItems: 'center',
                            justifyContent: 'space-between', marginBottom: 12,
                        }}>
                            <h2 style={{ fontSize: 20, fontWeight: 800, margin: 0, color: '#212529' }}>
                                {t('Transactions') || 'Transactions'}
                            </h2>
                            <button
                                type="button"
                                onClick={() => setShowTransactions(false)}
                                aria-label={t('close') || 'Close'}
                                style={{
                                    minWidth: 44, minHeight: 44, borderRadius: 12,
                                    border: '1px solid #e2e8f0', background: 'white',
                                    fontSize: 18, cursor: 'pointer', color: '#212529',
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <TransactionsPane tenantConfig={tenantConfig} t={t} />
                    </div>
                </div>,
                document.body,
            )}

        </div>


    );
};

export default SalesPage;
